import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, 
  Sparkles, 
  Moon, 
  Sun, 
  Zap, 
  Target, 
  Heart,
  Eye,
  Clock,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Lock,
  Mic,
  History,
  TrendingUp,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  Music,
  Waves,
  Brain,
  Settings2,
  Dumbbell,
  Trophy,
  Wind,
  Swords,
  Shield,
  Leaf,
  Flag,
  Infinity,
  Square,
  Home,
  Bookmark,
  Check
} from "lucide-react";
import axios from "axios";
import { voiceVolume, getStoredAudioMode } from "../utils/modeAudio";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { AchievementModal } from "../components/AchievementBadge";
import FloatingHomeButton from "../components/FloatingHomeButton";
import { trackAudioPlayed } from "../lib/dailyEssentialsTracker";

// Import refactored components
import {
  CURRENT_STATES,
  DESIRED_STATES,
  DURATIONS,
  MUSIC_STYLES,
  PERFORMANCE_ACTIVITIES,
  PERFORMANCE_FOCUS_AREAS,
  RECOVERY_EXPERIENCES,
  ATMOSPHERES,
  BRAIN_STATES,
  MODE_CONFIG,
  pageVariants,
  containerVariants,
  itemVariants,
  getModeStyles,
  formatTime,
  ModeSelection,
  DurationSelection,
  AffirmationEditor,
  VoiceSelection,
  RitualToggle,
  PrePerformanceRitual,
  PrivateSuiteOnboarding,
  SaveAsState,
} from "../components/PrivateSuite";
import TuneInToReality from "../components/PrivateSuite/RealityStates/TuneInToReality";
import MusicSelection from "../components/PrivateSuite/RealityStates/MusicSelection";
import BinauralPicker from "../components/PrivateSuite/BinauralPicker";
import ConfigureSession from "../components/PrivateSuite/ConfigureSession";
import SaveSessionToLibrary from "../components/PrivateSuite/SaveSessionToLibrary";
import SessionFeedbackCard from "../components/PrivateSuite/SessionFeedbackCard";
import PremiumOrb, { BINAURAL_HZ } from "../components/PremiumOrb";
import MyStates from "../components/PrivateSuite/MyStates";
import SignatureSelector, { SignatureFocusSelector } from "../components/PrivateSuite/SignatureSelector";
import { trackActivity } from "../components/ReclaimAssistant";
import { BottomNav } from "../components/BottomNav";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon dictionaries — extracted to a sibling module to begin the
// long-running decomposition of this file.
import { ACTIVITY_ICONS, RECOVERY_ICONS } from "../components/PrivateSuite/iconMaps";

/**
 * EditorialRow — Inner-Path styled list row used on the Private Suite
 * landing. Glass card · gold icon container · italic Cormorant title ·
 * gold chevron. Matches the visual language of /inner-path so the two
 * surfaces feel like one product.
 */
const EditorialRow = ({ icon, title, subtitle, onClick, testid }) => (
  <motion.button
    type="button"
    onClick={onClick}
    whileHover={{ y: -1 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    className="w-full text-left rounded-2xl p-4 flex items-center gap-4 group"
    style={{
      background: "linear-gradient(155deg, rgba(13, 36, 56, 0.78) 0%, rgba(10, 22, 38, 0.92) 100%)",
      border: "1px solid rgba(215, 187, 122, 0.10)",
    }}
    data-testid={testid}
  >
    <div
      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{
        background: "rgba(215, 187, 122, 0.10)",
        border: "1px solid rgba(215, 187, 122, 0.18)",
      }}
    >
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p
        className="mb-0.5"
        style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          color: "#F3EDDD",
          fontSize: "1.08rem",
          fontWeight: 400,
          lineHeight: 1.25,
        }}
      >
        {title}
      </p>
      <p
        className="italic"
        style={{
          color: "rgba(243, 237, 221, 0.48)",
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "0.85rem",
          lineHeight: 1.4,
        }}
      >
        {subtitle}
      </p>
    </div>
    <ChevronRight
      size={15}
      className="transition-transform group-hover:translate-x-0.5"
      style={{ color: "rgba(215, 187, 122, 0.55)" }}
    />
  </motion.button>
);

/**
 * EditorialStepHeader — shared top block for each Configure Session
 * inner screen (Mode → Activity → Focus → Voice → Duration). Replaces
 * the per-mode gradient-colour eyebrow with a unified gold tracked
 * eyebrow + Cormorant Garamond display heading + italic subhead.
 */
const EditorialStepHeader = ({ onBack, eyebrow, title, subtitle, backTestid }) => (
  <div className="mb-8">
    {onBack && (
      <button
        onClick={onBack}
        className="mb-6 flex items-center justify-center w-9 h-9 rounded-full transition-colors"
        style={{
          background: "rgba(215, 187, 122, 0.08)",
          border: "1px solid rgba(215, 187, 122, 0.22)",
          color: "#D7BB7A",
        }}
        data-testid={backTestid || "editorial-back-btn"}
        aria-label="Back"
      >
        <ArrowLeft size={16} />
      </button>
    )}
    {eyebrow && (
      <p
        className="uppercase tracking-[0.4em] mb-3"
        style={{ color: "#D7BB7A", fontFamily: '"Manrope", sans-serif', fontSize: "0.6rem" }}
      >
        {eyebrow}
      </p>
    )}
    {title && (
      <h2
        className="mb-2"
        style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: "clamp(1.9rem, 4.6vw, 2.6rem)",
          fontWeight: 300,
          color: "#F3EDDD",
          letterSpacing: "0.01em",
          lineHeight: 1.18,
        }}
      >
        {title}
      </h2>
    )}
    {subtitle && (
      <p
        className="italic"
        style={{
          color: "rgba(243, 237, 221, 0.5)",
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "1rem",
          lineHeight: 1.55,
        }}
      >
        {subtitle}
      </p>
    )}
  </div>
);

/**
 * EditorialSelectCard — generic selectable card for Mode / Activity /
 * Voice / Duration. Gold-only accent, italic Cormorant Garamond title,
 * gold check on select, optional chip strip beneath the subtitle for
 * meta tags (e.g. activity counts on Performance mode).
 */
const EditorialSelectCard = ({
  icon, title, subtitle, chips, selected, onClick, testid, badge, disabled, badgeColor = "#D7BB7A",
}) => (
  <motion.button
    type="button"
    onClick={onClick}
    disabled={disabled}
    whileHover={!disabled ? { y: -1 } : undefined}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    className="w-full text-left rounded-2xl p-5 flex items-start gap-4 group transition-all"
    style={{
      background: selected
        ? "linear-gradient(155deg, rgba(215, 187, 122, 0.18) 0%, rgba(13, 36, 56, 0.92) 100%)"
        : "linear-gradient(155deg, rgba(13, 36, 56, 0.78) 0%, rgba(10, 22, 38, 0.92) 100%)",
      border: selected
        ? "1px solid rgba(215, 187, 122, 0.45)"
        : "1px solid rgba(215, 187, 122, 0.10)",
      boxShadow: selected ? "0 0 22px rgba(215, 187, 122, 0.18)" : "none",
      opacity: disabled ? 0.45 : 1,
      cursor: disabled ? "not-allowed" : "pointer",
    }}
    data-testid={testid}
    data-selected={selected || undefined}
  >
    {icon && (
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{
          background: selected ? "rgba(215, 187, 122, 0.22)" : "rgba(215, 187, 122, 0.10)",
          border: "1px solid rgba(215, 187, 122, 0.22)",
        }}
      >
        {icon}
      </div>
    )}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <p
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            color: "#F3EDDD",
            fontSize: "1.18rem",
            fontWeight: 400,
            lineHeight: 1.25,
          }}
        >
          {title}
        </p>
        {badge && (
          <span
            className="text-[10px] px-2 py-0.5 rounded-full uppercase"
            style={{
              fontFamily: '"Manrope", sans-serif',
              letterSpacing: "0.18em",
              background: `${badgeColor}22`,
              color: badgeColor,
              border: `1px solid ${badgeColor}44`,
            }}
          >
            {badge}
          </span>
        )}
      </div>
      {subtitle && (
        <p
          className="italic"
          style={{
            color: "rgba(243, 237, 221, 0.5)",
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "0.9rem",
            lineHeight: 1.4,
          }}
        >
          {subtitle}
        </p>
      )}
      {chips && chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {chips.map((c, i) => (
            <span
              key={i}
              className="text-[10px] px-2 py-0.5 rounded-full uppercase"
              style={{
                fontFamily: '"Manrope", sans-serif',
                letterSpacing: "0.12em",
                background: "rgba(215, 187, 122, 0.08)",
                color: "rgba(215, 187, 122, 0.7)",
                border: "1px solid rgba(215, 187, 122, 0.14)",
              }}
            >
              {c}
            </span>
          ))}
        </div>
      )}
    </div>
    {selected && (
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 self-center"
        style={{
          background: "linear-gradient(135deg, #F5E0AD 0%, #A88A4C 100%)",
        }}
      >
        <Check size={14} style={{ color: "#0a1a2e", strokeWidth: 3 }} />
      </div>
    )}
    {!selected && !disabled && (
      <ChevronRight
        size={16}
        className="self-center transition-transform group-hover:translate-x-0.5 flex-shrink-0"
        style={{ color: "rgba(215, 187, 122, 0.45)" }}
      />
    )}
  </motion.button>
);

const PrivateSuitePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get intent from Alignment Check (if coming from there)
  const alignmentIntent = searchParams.get('intent');
  const alignmentLength = searchParams.get('length');
  const fromAlignmentCheck = !!alignmentIntent;
  
  // Mapping: Desired State → Mode
  const INTENT_TO_MODE = {
    calm: "meditation",
    clarity: "meditation",
    peace: "meditation",
    joy: "meditation",
    connection: "meditation",
    confidence: "performance",
    energy: "performance",
    focus: "performance",
  };
  
  // Mapping: Desired State → Audio Settings
  const INTENT_TO_AUDIO = {
    calm: { brainState: "alpha", atmosphere: "ambient" },
    clarity: { brainState: "alpha", atmosphere: "nature" },
    peace: { brainState: "theta", atmosphere: "ambient" },
    joy: { brainState: "alpha", atmosphere: "nature" },
    connection: { brainState: "alpha", atmosphere: "ambient" },
    confidence: { brainState: "beta", atmosphere: "ambient" },
    energy: { brainState: "beta", atmosphere: "nature" },
    focus: { brainState: "beta", atmosphere: "ambient" },
  };
  
  // Steps: 0: entry, 0.5: mode select, 1: activity/state selection, 2: focus/desired state
  // 2.5: affirmations, 2.6: ritual toggle (performance only), 2.7: voice selection, 3: music/duration, 
  // 3.5: ritual player (if enabled), 4: session, 5: history
  const [step, setStep] = useState(0);
  
  // First-time onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showSaveState, setShowSaveState] = useState(false);
  const [hasCompletedFirstSession, setHasCompletedFirstSession] = useState(false);
  
  // Reality States mode
  const [showRealityFlow, setShowRealityFlow] = useState(false);
  const [savedRealityStates, setSavedRealityStates] = useState([]);
  
  // My States dashboard
  const [showMyStates, setShowMyStates] = useState(false);
  
  // Mode selection: meditation, performance, deep_recovery, reality
  const [mode, setMode] = useState(null);
  
  // Meditation mode state
  const [currentState, setCurrentState] = useState(null);
  const [desiredState, setDesiredState] = useState(null);
  
  // Performance mode state
  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [focusAreas, setFocusAreas] = useState([]);
  const [selectedFocusArea, setSelectedFocusArea] = useState(null);
  const [musicStyle, setMusicStyle] = useState("edm");
  // Premium music selection — opens the Collective-aesthetic 15-cat picker.
  const [showMusicPicker, setShowMusicPicker] = useState(false);
  const [premiumMusicTrack, setPremiumMusicTrack] = useState(null); // { id, name, displayName, url }

  // Binaural Beats layer — separate from music. User can mix any band
  // under any chosen music track. `binauralBeat` is `{ id, label, url }`
  // or null; `binauralEnabled` is the master toggle.
  const [binauralEnabled, setBinauralEnabled] = useState(false);
  const [binauralBeat, setBinauralBeat] = useState(null);
  // Volume sits subliminally UNDER the music. User feedback Feb 11 2026:
  // "Binaural beats are great, just a little loud, let's lower the volume."
  // Was 0.2 — dropped to 0.12 so the entrainment is felt, not heard.
  const [binauralVolume, setBinauralVolume] = useState(0.12);
  const [showBinauralPicker, setShowBinauralPicker] = useState(false);
  const binauralAudioRef = useRef(null);

  // (Whisper volume slider was removed — the Subliminal Mode toggle in
  // Step 3 provides the two precise volume presets we actually need.
  // The server applies -28dB pre-reduction for the subliminal file.)

  const [customAffirmations, setCustomAffirmations] = useState([]); // User's editable affirmations
  const [editableAffirmations, setEditableAffirmations] = useState([]); // Unified editable affirmations for all modes
  const [suggestedAffirmations, setSuggestedAffirmations] = useState([]); // Loaded from backend
  
  // Signature Mode state
  const [signatureData, setSignatureData] = useState(null);
  const [signatureMode, setSignatureMode] = useState(false); // true = user chose Signature
  const [selectedFocusAreaName, setSelectedFocusAreaName] = useState(null);
  
  // Pre-Performance Ritual state
  const [ritualEnabled, setRitualEnabled] = useState(true); // Default to enabled
  const [ritualData, setRitualData] = useState(null); // Ritual script from backend
  const [showingRitual, setShowingRitual] = useState(false); // Currently showing ritual player
  
  // Pre-recorded Audio state (for competition/range practice)
  const [preRecordedAudioUrl, setPreRecordedAudioUrl] = useState(null);
  const [preRecordedAudioLabel, setPreRecordedAudioLabel] = useState(null);
  const [preRecordedAudioDuration, setPreRecordedAudioDuration] = useState(null);
  
  // Deep Recovery mode state
  const [recoveryExperiences, setRecoveryExperiences] = useState([]);
  const [selectedRecoveryExperience, setSelectedRecoveryExperience] = useState(null);
  
  // Health Recovery state
  const [healthCondition, setHealthCondition] = useState("");
  const [healthAffirmations, setHealthAffirmations] = useState([]);
  const [generatingHealthAffirmations, setGeneratingHealthAffirmations] = useState(false);
  const [healthAssistantResponse, setHealthAssistantResponse] = useState(null);
  const [healthStep, setHealthStep] = useState(1); // 1: feeling, 2: context, 3: desired state
  const [healthFeeling, setHealthFeeling] = useState(null);
  const [healthContext, setHealthContext] = useState(null);
  const [healthDesiredState, setHealthDesiredState] = useState(null);
  const [healthFreeText, setHealthFreeText] = useState("");
  
  // Health Recovery Options
  const HEALTH_FEELINGS = [
    { id: "uncomfortable", label: "Physically uncomfortable", icon: "😣" },
    { id: "pain", label: "In pain", icon: "💔" },
    { id: "anxious", label: "Anxious / overwhelmed", icon: "😰" },
    { id: "tired", label: "Tired / drained", icon: "😴" },
    { id: "calm_rest", label: "Calm but want deeper rest", icon: "🌙" },
    { id: "other", label: "Other", icon: "💭" }
  ];
  
  const HEALTH_CONTEXTS = [
    { id: "physical", label: "Physical discomfort / pain" },
    { id: "surgery", label: "Surgery or medical recovery" },
    { id: "hormonal", label: "Hormonal / internal imbalance" },
    { id: "emotional", label: "Emotional stress / trauma" },
    { id: "fatigue", label: "Fatigue / burnout" },
    { id: "skip", label: "Prefer not to say" }
  ];
  
  const HEALTH_DESIRED_STATES = [
    { id: "calm", label: "Calm", icon: "🌊" },
    { id: "safety", label: "Safety", icon: "🛡️" },
    { id: "relief", label: "Relief", icon: "✨" },
    { id: "softness", label: "Softness", icon: "🌸" },
    { id: "trust", label: "Trust", icon: "🤝" },
    { id: "sleep", label: "Sleep / deep rest", icon: "💤" },
    { id: "letting_go", label: "Letting go", icon: "🍃" }
  ];
  
  // Shared state
  const [duration, setDuration] = useState(null);
  // Custom duration (minutes) — wired up when user picks the "custom"
  // option in the DURATIONS list. Range 1–60. Default 10 mins.
  const [customDurationMinutes, setCustomDurationMinutes] = useState(10);
  // `subliminalMode` is derived from the whisper slider — when the user
  // pulls it below 1% the affirmations are subliminal by definition.
  // Kept as state (not just a getter) so request payloads, history and
  // UI labels stay readable. Updated automatically by the slider
  // useEffect below.
  const [subliminalMode, setSubliminalMode] = useState(false);
  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState(null);
  const [hasAffirmations, setHasAffirmations] = useState(true);
  const [voiceOption, setVoiceOption] = useState("default");
  const [affirmationSource, setAffirmationSource] = useState("reclaim"); // "reclaim", "own", "mix"
  const [isEntering, setIsEntering] = useState(false);
  // D3.54 — Begin Session signature dot expansion portal
  const [beginExpanding, setBeginExpanding] = useState(false);
  const [postSessionFeeling, setPostSessionFeeling] = useState(null);
  // Premium tier flag — drives the "Download MP3" CTA after a session
  // (premium-only) AND the Library list visibility. Hydrated below.
  const [isPremiumUser, setIsPremiumUser] = useState(false);
  
  // AI Voice Cloning state (Premium feature)
  const [aiVoiceUsage, setAiVoiceUsage] = useState(null);
  const [aiVoiceLoading, setAiVoiceLoading] = useState(false);
  const [generatingAiVoice, setGeneratingAiVoice] = useState(false);
  const [playingAiDemo, setPlayingAiDemo] = useState(false);
  const aiDemoAudioRef = useRef(null);
  
  // Recording state (for "Record Own Voice" option)
  const [isRecording, setIsRecording] = useState(false);
  const [recordingBlob, setRecordingBlob] = useState(null);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [recordingError, setRecordingError] = useState(null);
  const [currentReadingIndex, setCurrentReadingIndex] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingPlaybackRef = useRef(null);
  const [isPlayingRecording, setIsPlayingRecording] = useState(false);
  
  // Audio customization options
  const [atmosphere, setAtmosphere] = useState("ambient");
  const [brainState, setBrainState] = useState("alpha");
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // History and insights state
  const [sessionHistory, setSessionHistory] = useState([]);
  const [insights, setInsights] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Milestone celebration state
  const [showMilestoneCelebration, setShowMilestoneCelebration] = useState(false);
  const [newMilestone, setNewMilestone] = useState(null);
  const [totalMinutesForMilestone, setTotalMinutesForMilestone] = useState(0);
  
  // Session player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [sessionPhase, setSessionPhase] = useState("intro"); // intro, breathing, affirmations, complete, reflection
  const [breathPhase, setBreathPhase] = useState(null);
  const [breathCount, setBreathCount] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [currentAffirmationText, setCurrentAffirmationText] = useState(null); // Currently playing affirmation for display
  
  // Audio refs
  const introAudioRef = useRef(null);
  const ambientAudioRef = useRef(null);
  const affirmationAudioRef = useRef(null);
  const voiceTrackAudioRef = useRef(null);  // Michael's pre-recorded voice tracks
  const startSessionRef = useRef(null); // Ref to call startSession from alignment flow
  const breathingAudioRef = useRef(null);
  const timerRef = useRef(null);
  const breathTimerRef = useRef(null);
  const elapsedTimeRef = useRef(0);  // Track elapsed time for accurate completion timing
  const sessionStartTimeRef = useRef(null);  // Track when session started
  const isPlayingRef = useRef(false);  // Track playing state to avoid stale closure
  const subliminalModeRef = useRef(false);  // Track subliminal mode for audio volume

  // Keep subliminalModeRef in sync with the toggle state. The toggle
  // is the single source of truth (slider was unreliable on iOS).
  useEffect(() => {
    subliminalModeRef.current = subliminalMode;
  }, [subliminalMode]);

  // Breathing audio URLs
  const THREE_BREATHS_AUDIO = `${API_URL}/api/breathing-audio/3_breaths.m4a`;
  const TWO_BREATHS_AUDIO = `${API_URL}/api/breathing-audio/2_breaths.m4a`;

  const getToken = () => localStorage.getItem("reclaim_token");

  // Check access on mount
  useEffect(() => {
    checkAccess();
  }, []);

  // Load insights when access is confirmed
  useEffect(() => {
    if (hasAccess) {
      loadInsights();
    }
  }, [hasAccess]);

  // Initialize from Alignment Check (auto-configure when coming from there)
  // This runs for BOTH premium and free users coming from Alignment Check
  // Track if alignment initialization has run
  const alignmentInitRef = useRef(false);
  
  useEffect(() => {
    if (!loading && fromAlignmentCheck && alignmentIntent && !alignmentInitRef.current) {
      alignmentInitRef.current = true; // Prevent multiple calls
      initializeFromAlignmentCheck();
    }
  }, [loading, fromAlignmentCheck, alignmentIntent]);

  // Initialize from Aurum Private Suite — skip the entry + mode picker and land
  // the user directly on the first config step of the requested mode. For
  // performance mode, optionally also skip the Activity picker by reading the
  // `activity` URL param (set by Aurum tiles like General Performance / Sleep,
  // or by the Golf intro screen).
  const aurumInitRef = useRef(false);
  useEffect(() => {
    if (loading || aurumInitRef.current) return;
    const from = searchParams.get('from');
    if (from !== 'aurum' && from !== 'golf') return;
    // Golf entry uses sensible defaults so the user lands straight in the
    // Performance Signature flow rather than the main mode menu.
    const wantedMode = searchParams.get('mode') || (from === 'golf' ? 'performance' : null);
    if (!wantedMode) return;
    aurumInitRef.current = true;
    const MODE_ALIASES = {
      meditation: 'meditation',
      performance: 'performance',
      deep_recovery: 'deep_recovery',
      health_recovery: 'health_recovery',
      reality: 'reality',
    };
    const m = MODE_ALIASES[wantedMode];
    if (!m) return;
    // Preserve the entry intent so downstream flows can tweak theming if needed.
    try {
      if (from === 'aurum') sessionStorage.setItem('private_suite_from_aurum', '1');
      if (from === 'golf')  sessionStorage.setItem('private_suite_from_golf', '1');
    } catch (_) { /* noop */ }
    // Run the same logic as handleModeSelect, without waiting for the user tap.
    setMode(m);
    if (m === 'reality') {
      loadSavedRealityStates();
      setShowRealityFlow(true);
    } else if (m === 'performance') {
      // Aurum's General Performance / Sleep / Confidence tiles and the Golf
      // intro both preset the activity so we can deep-link past the activity
      // picker (step 1) and land on the Signature/Custom selector (step 1.5).
      // Default activity for golf entry is `golf` so the user lands directly
      // in the golf signature flow.
      const presetActivity = searchParams.get('activity')
        || (from === 'golf' ? 'golf' : null);
      loadPerformanceActivities();
      if (presetActivity) {
        setSelectedActivity(presetActivity);
        loadFocusAreas(presetActivity);
        loadSignatureData(presetActivity);
        setStep(1.5);
      } else {
        setStep(1);
      }
    } else if (m === 'deep_recovery') {
      loadRecoveryExperiences();
      setStep(1);
    } else {
      setStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, searchParams]);
  
  // Auto-configure session from Alignment Check intent
  const initializeFromAlignmentCheck = async () => {
    const intent = alignmentIntent;
    const autoMode = INTENT_TO_MODE[intent] || "meditation";
    const audioSettings = INTENT_TO_AUDIO[intent] || { brainState: "alpha", atmosphere: "ambient" };
    
    // Set mode and audio settings
    setMode(autoMode);
    setBrainState(audioSettings.brainState);
    setAtmosphere(audioSettings.atmosphere);
    setDesiredState(intent);
    
    // Set duration from alignment check (if passed)
    if (alignmentLength) {
      const durationMap = {
        "5": { id: "5min", seconds: 300 },
        "10": { id: "10min", seconds: 600 },
        "20": { id: "20min", seconds: 1200 },
        "30": { id: "30min", seconds: 1800 },
      };
      const durConfig = durationMap[alignmentLength];
      if (durConfig) {
        setDuration(durConfig);
      }
    }
    
    // Load affirmations for the intent
    try {
      const response = await axios.get(
        `${API_URL}/api/private-suite/user-affirmations?desired_state=${intent}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      if (response.data.affirmations && response.data.affirmations.length > 0) {
        // User affirmations API returns objects with {text, audio_url, ...}
        setEditableAffirmations(response.data.affirmations.map(aff => ({
          text: typeof aff === 'string' ? aff : (aff.text || String(aff)),
          enabled: true
        })));
      } else {
        // Load default affirmations for this state using the working endpoint
        try {
          const defaultRes = await axios.get(
            `${API_URL}/api/private-suite/meditation/affirmations/${intent}`,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );
          
          const defaultAffirmations = defaultRes.data?.affirmations || [];
          if (defaultAffirmations.length > 0) {
            setEditableAffirmations(defaultAffirmations.map(a => ({
              text: typeof a === 'string' ? a : (a.text || String(a)),
              enabled: true
            })));
          } else {
            // Use hardcoded defaults
            const hardcodedDefaults = getDefaultAffirmationsForIntent(intent);
            setEditableAffirmations(hardcodedDefaults.map(text => ({
              text,
              enabled: true
            })));
          }
        } catch {
          // Use hardcoded defaults on API error
          const hardcodedDefaults = getDefaultAffirmationsForIntent(intent);
          setEditableAffirmations(hardcodedDefaults.map(text => ({
            text,
            enabled: true
          })));
        }
      }
    } catch (err) {
      console.error("Error loading affirmations for intent:", err);
      // Set default affirmations for the intent
      const defaultAffirmations = getDefaultAffirmationsForIntent(intent);
      setEditableAffirmations(defaultAffirmations.map(text => ({
        text,
        enabled: true
      })));
    }
    
    // Auto-generate session and go directly to breathing → affirmations
    // This bypasses: entry (0), mode select (0.5), mood check (1), desired state (2), affirmation editing (2.5), music selection (3)
    await autoGenerateSession(autoMode, intent, audioSettings);
  };
  
  // Auto-generate session from alignment check (skip all config steps)
  const autoGenerateSession = async (autoMode, intent, audioSettings) => {
    setLoading(true);
    try {
      // Get default affirmations for the intent
      const defaultAffirmations = getDefaultAffirmationsForIntent(intent);
      
      // Get duration from URL params or use 5 minutes default (300 seconds)
      const urlLength = searchParams.get('length');
      const sessionDuration = urlLength ? parseInt(urlLength) * 60 : 300;
      
      // Build request with proper values
      const requestData = {
        mode: autoMode,
        duration_seconds: sessionDuration, // Backend expects duration_seconds
        subliminal_mode: false,
        voice_option: "reclaim", // Use Reclaim voice
        current_state: "stressed", // Default starting state
        desired_state: intent,
        atmosphere: audioSettings?.atmosphere || "ambient",
        brain_state: audioSettings?.brainState || "alpha",
        custom_affirmations: defaultAffirmations,
      };
      
      console.log("[Alignment → Session] Auto-generating session:", requestData);
      
      const response = await axios.post(
        `${API_URL}/api/private-suite/generate-session`,
        requestData,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      setSessionData(response.data);
      setStep(4); // Go directly to session (starts with breathing)
      
      // Auto-start the session after a brief moment
      setTimeout(() => {
        startSessionRef.current?.();
      }, 500);
      
    } catch (err) {
      console.error("Error auto-generating session:", err);
      toast.error("Failed to start session. Please try again.");
      // Fallback to affirmations step so user can manually proceed
      setStep(2.5);
    } finally {
      setLoading(false);
    }
  };
  
  // Default health recovery affirmations (bilingual - English then Spanish)
  const DEFAULT_HEALTH_AFFIRMATIONS = [
    "I am safe in this moment. Estoy a salvo en este momento.",
    "My body is allowed to relax now. Mi cuerpo puede relajarse ahora.",
    "I gently release all tension from my body. Suelto suavemente toda la tensión de mi cuerpo.",
    "Every breath softens me deeper. Cada respiración me suaviza más profundamente.",
    "I trust my body to guide me back to balance. Confío en que mi cuerpo me guía de vuelta al equilibrio.",
    "I do not need to hold anything right now. No necesito sostener nada en este momento.",
    "My body knows how to heal. Mi cuerpo sabe cómo sanar.",
    "I allow everything to slow down. Permito que todo se ralentice.",
    "I am supported, held, and cared for. Estoy apoyada, sostenida y cuidada.",
    "With every exhale, I let go. Con cada exhalación, suelto.",
    "I am safe to rest deeply. Es seguro para mí descansar profundamente.",
    "My body is softening and opening. Mi cuerpo se suaviza y se abre.",
    "Healing is happening naturally within me. La sanación ocurre naturalmente dentro de mí.",
    "I allow comfort to flow through my body. Permito que el confort fluya por mi cuerpo.",
    "I am not alone in this moment. No estoy sola en este momento.",
    "My breath is calm, slow, and steady. Mi respiración es tranquila, lenta y constante.",
    "I release fear and return to presence. Suelto el miedo y vuelvo a la presencia.",
    "My body is working for me, not against me. Mi cuerpo trabaja para mí, no en mi contra.",
    "I allow myself to sink into deep rest. Me permito hundirme en un descanso profundo.",
    "All is well in this moment. Todo está bien en este momento."
  ];

  // Default affirmations fallback for each intent
  const getDefaultAffirmationsForIntent = (intent) => {
    const defaults = {
      calm: [
        "I am at peace with this moment",
        "I release tension with each breath",
        "My mind is calm and clear",
        "I trust in the process of life",
        "Serenity flows through me"
      ],
      clarity: [
        "My thoughts are clear and focused",
        "I see my path with perfect clarity",
        "Wisdom flows through me effortlessly",
        "I trust my inner knowing",
        "My mind is sharp and receptive"
      ],
      confidence: [
        "I am worthy of success and happiness",
        "I trust in my abilities completely",
        "I face challenges with strength and grace",
        "My presence commands respect",
        "I am confident in who I am"
      ],
      energy: [
        "Vital energy flows through every cell",
        "I am alive with possibility",
        "My body and mind are energized",
        "I embrace this moment with enthusiasm",
        "Power and vitality are my natural state"
      ],
      focus: [
        "I direct my attention with precision",
        "My mind is a laser of concentration",
        "I am fully present in this moment",
        "Distractions dissolve in my presence",
        "I accomplish what I set out to do"
      ],
      peace: [
        "I am at peace with myself",
        "Harmony surrounds and fills me",
        "I release what no longer serves me",
        "Inner peace is my natural state",
        "I rest in the stillness within"
      ],
      joy: [
        "Joy flows naturally through me",
        "I choose happiness in this moment",
        "My heart is light and open",
        "I celebrate the gift of being alive",
        "Gratitude fills every moment"
      ],
      connection: [
        "I am deeply connected to all life",
        "Love flows freely through me",
        "I am one with the universe",
        "My heart is open to giving and receiving",
        "I honor my connection to all beings"
      ]
    };
    return defaults[intent] || defaults.calm;
  };

  const checkAccess = async () => {
    // Always grant access - bypassing API check
    setHasAccess(true);
    setLoading(false);

    // Still load insights and activities in background
    try {
      const response = await axios.get(`${API_URL}/api/private-suite/insights`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setInsights(response.data);
    } catch (err) {
      console.log("Could not load insights");
    }

    // Hydrate premium flag — gates the post-session "Download MP3"
    // CTA. Fail-soft to free so non-paying users can't accidentally
    // hit a premium endpoint.
    try {
      const me = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const tier = (me.data?.membership_tier || "free").toLowerCase();
      setIsPremiumUser(["premium", "inner_circle", "admin"].includes(tier));
    } catch (err) {
      // best-effort; non-premium is the safe default
      setIsPremiumUser(false);
    }
  };

  const loadInsights = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/private-suite/insights`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setInsights(response.data);
    } catch (err) {
      console.error("Error loading insights:", err);
    }
  };

  // Load performance activities
  const loadPerformanceActivities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/private-suite/performance/activities`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setActivities(response.data.activities);
    } catch (err) {
      console.error("Error loading activities:", err);
    }
  };

  // Load focus areas for selected activity
  const loadFocusAreas = async (activityId) => {
    try {
      const response = await axios.get(`${API_URL}/api/private-suite/performance/focus-areas/${activityId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setFocusAreas(response.data.focus_areas);
    } catch (err) {
      console.error("Error loading focus areas:", err);
    }
  };

  // Load recovery experiences
  const loadRecoveryExperiences = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/private-suite/recovery/experiences`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setRecoveryExperiences(response.data.experiences);
    } catch (err) {
      console.error("Error loading recovery experiences:", err);
    }
  };

  // Load suggested affirmations for performance mode
  const loadSuggestedAffirmations = async (activity, focusArea) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/private-suite/performance/affirmations/${activity}/${focusArea}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const affirmations = response.data.affirmations || [];
      setSuggestedAffirmations(affirmations);
      // Initialize custom affirmations with suggested ones (users can edit)
      setCustomAffirmations(affirmations.map((aff, idx) => ({ 
        id: idx, 
        text: typeof aff === 'string' ? aff : (aff.text || String(aff)), 
        enabled: true 
      })));
      // Also set editableAffirmations for the unified affirmation editing step (2.5)
      setEditableAffirmations(affirmations.map(aff => ({ 
        text: typeof aff === 'string' ? aff : (aff.text || String(aff)), 
        enabled: true 
      })));
    } catch (err) {
      console.error("Error loading affirmations:", err);
      // Fallback to empty array
      setCustomAffirmations([]);
      setEditableAffirmations([]);
    }
  };

  // Add a new custom affirmation
  const addCustomAffirmation = () => {
    const newId = customAffirmations.length;
    setCustomAffirmations([...customAffirmations, { id: newId, text: "", enabled: true }]);
  };

  // Update an affirmation
  const updateAffirmation = (id, text) => {
    setCustomAffirmations(prev => 
      prev.map(aff => aff.id === id ? { ...aff, text } : aff)
    );
  };

  // Toggle affirmation enabled/disabled
  const toggleAffirmation = (id) => {
    setCustomAffirmations(prev => 
      prev.map(aff => aff.id === id ? { ...aff, enabled: !aff.enabled } : aff)
    );
  };

  // Remove an affirmation
  const removeAffirmation = (id) => {
    setCustomAffirmations(prev => prev.filter(aff => aff.id !== id));
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const [historyRes, insightsRes] = await Promise.all([
        axios.get(`${API_URL}/api/private-suite/history?limit=10`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        }),
        axios.get(`${API_URL}/api/private-suite/insights`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        })
      ]);
      setSessionHistory(historyRes.data.sessions);
      setInsights(insightsRes.data);
    } catch (err) {
      console.error("Error loading history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Load and start a replay session
  const startReplaySession = async (sessionId) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/private-suite/session/${sessionId}/replay`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      // Set mode from the replay data
      setMode(response.data.mode);
      setSessionData(response.data);
      setStep(4); // Go to session player
      
      toast.success("Starting replay session");
    } catch (err) {
      console.error("Error loading replay session:", err);
      toast.error("Failed to load session");
    } finally {
      setLoading(false);
    }
  };

  // Get mode-specific icon for history
  const getModeIcon = (session) => {
    if (session.mode === "performance") {
      const ActivityIcon = ACTIVITY_ICONS[session.activity] || Trophy;
      return ActivityIcon;
    } else if (session.mode === "deep_recovery") {
      const RecoveryIcon = RECOVERY_ICONS[session.recovery_experience] || Shield;
      return RecoveryIcon;
    } else {
      return Heart;
    }
  };

  // Get display title for session
  const getSessionTitle = (session) => {
    if (session.mode === "performance") {
      return session.activity_label || session.activity || "Performance";
    } else if (session.mode === "deep_recovery") {
      return session.recovery_label || "Recovery";
    } else {
      return session.desired_state_label || "Meditation";
    }
  };

  // Get display subtitle for session
  const getSessionSubtitle = (session) => {
    if (session.mode === "performance") {
      return session.focus_label || session.focus_area || "";
    } else if (session.mode === "deep_recovery") {
      return "Deep Recovery";
    } else {
      return session.current_state_label ? `From ${session.current_state_label}` : "";
    }
  };

  // Get mode color
  const getModeColor = (session) => {
    if (session.mode === "performance") {
      return "from-orange-500/20 to-red-500/20";
    } else if (session.mode === "deep_recovery") {
      return "from-slate-500/20 to-indigo-500/20";
    } else {
      return "from-violet-500/20 to-purple-500/20";
    }
  };

  // Check for user affirmations when desired state is selected
  useEffect(() => {
    if (desiredState) {
      checkAffirmations();
    }
  }, [desiredState]);

  const checkAffirmations = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/private-suite/user-affirmations?desired_state=${desiredState}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setHasAffirmations(response.data.has_affirmations);
    } catch (err) {
      console.error("Error checking affirmations:", err);
    }
  };

  // Gentle entry transition - now goes to mode selection
  const handleBeginExperience = () => {
    // Check if first time user
    const hasSeenOnboarding = localStorage.getItem('privateSuiteOnboardingComplete');
    
    if (!hasSeenOnboarding) {
      setShowOnboarding(true);
    } else {
      setIsEntering(true);
      setTimeout(() => {
        setStep(0.5); // Mode selection step
        setIsEntering(false);
      }, 500);
    }
  };
  
  // Handle onboarding completion
  const handleOnboardingComplete = (data) => {
    localStorage.setItem('privateSuiteOnboardingComplete', 'true');
    setShowOnboarding(false);
    
    // Pre-set the selections from onboarding
    if (data.currentFeeling) {
      setCurrentState(data.currentFeeling);
    }
    if (data.desiredFeeling) {
      setDesiredState(data.desiredFeeling);
      // Auto-load affirmations for the desired state
      loadAffirmationsForDesiredState(data.desiredFeeling);
    }
    
    // Set mode to meditation and go to affirmations step
    setMode('meditation');
    setStep(2.5); // Go to affirmation editing
  };
  
  // Load affirmations for a desired state
  const loadAffirmationsForDesiredState = async (desiredStateId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/private-suite/meditation/affirmations/${desiredStateId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setEditableAffirmations(response.data.affirmations.map(aff => ({
        text: typeof aff === 'string' ? aff : (aff.text || String(aff)),
        enabled: true
      })));
    } catch (err) {
      console.error("Error loading affirmations:", err);
      const hardcodedDefaults = getDefaultAffirmationsForIntent(desiredStateId);
      setEditableAffirmations(hardcodedDefaults.map(text => ({
        text,
        enabled: true
      })));
    }
  };
  
  // Handle onboarding skip
  const handleOnboardingSkip = () => {
    localStorage.setItem('privateSuiteOnboardingComplete', 'true');
    setShowOnboarding(false);
    setIsEntering(true);
    setTimeout(() => {
      setStep(0.5); // Mode selection step
      setIsEntering(false);
    }, 500);
  };
  
  // Handle save state after session
  const handleSaveState = async (stateData) => {
    try {
      await axios.post(
        `${API_URL}/api/private-suite/states`,
        {
          name: stateData.name,
          desired_state: stateData.desiredState,
          mode: mode,
          affirmations: editableAffirmations.filter(a => a.enabled).map(a => a.text),
          duration: 15,
          voice_type: 'reclaim',
          subliminal: subliminalMode
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setHasCompletedFirstSession(true);
    } catch (err) {
      console.error("Error saving state:", err);
      throw err;
    }
  };

  // Handle mode selection
  const handleModeSelect = (selectedMode) => {
    setMode(selectedMode);
    
    if (selectedMode === "meditation") {
      setStep(1); // Go to emotional check-in
    } else if (selectedMode === "performance") {
      loadPerformanceActivities();
      setStep(1); // Will show activity selection
    } else if (selectedMode === "deep_recovery") {
      loadRecoveryExperiences();
      setStep(1); // Will show experience selection
    } else if (selectedMode === "health_recovery") {
      setStep(1); // Will show health recovery options
    } else if (selectedMode === "reality") {
      // Load saved reality states and show the flow
      loadSavedRealityStates();
      setShowRealityFlow(true);
    }
  };

  // Direct entry to Performance — skips mode selection
  const handleEnterPerformance = () => {
    setIsEntering(true);
    setMode("performance");
    loadPerformanceActivities();
    setTimeout(() => {
      setStep(1);
      setIsEntering(false);
    }, 500);
  };
  
  // Load saved reality states from backend
  const loadSavedRealityStates = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/private-suite/reality-states`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setSavedRealityStates(response.data.states || []);
    } catch (err) {
      console.error("Error loading reality states:", err);
      setSavedRealityStates([]);
    }
  };
  
  // Save a new reality state
  const handleSaveRealityState = async (stateData) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/private-suite/reality-states`,
        stateData,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      // Update local state with the new saved state
      setSavedRealityStates(prev => [...prev, response.data.state]);
      toast.success("Reality state saved");
      
      // Track activity
      trackActivity("reality_state_created", { archetype: stateData.archetypeId });
      
      return response.data.state;
    } catch (err) {
      console.error("Error saving reality state:", err);
      toast.error("Failed to save state");
      throw err;
    }
  };
  
  // Close reality flow
  const handleCloseRealityFlow = () => {
    setShowRealityFlow(false);
    setMode(null);
    setStep(0.5); // Return to mode selection
  };

  // Handle activity selection (performance mode)
  const handleActivitySelect = (activity) => {
    setSelectedActivity(activity);
    loadFocusAreas(activity);
    loadSignatureData(activity);
    setStep(1.5); // Go to Signature vs Custom selector
  };

  // Load signature profile for the selected sport
  const loadSignatureData = async (sportId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/universal-audio/signatures/${sportId}`
      );
      setSignatureData(response.data);
    } catch (err) {
      console.error("Error loading signature:", err);
      setSignatureData(null);
    }
  };

  // Handle Signature Mode selection.
  // Same as focus-area select but for sports with a single signature
  // file (no focus areas). Routes through the full session player.
  const handleSignatureSelect = async () => {
    if (!signatureData?.has_audio) return;
    setSignatureMode(true);
    setSelectedFocusAreaName(null);
    // Multi-focus sports: go to the focus-area picker, then the picker
    // routes through the full session flow via handleSignatureFocusSelect.
    if (signatureData.has_focus_areas && signatureData.focus_areas?.length > 0) {
      setStep(1.6);
      return;
    }
    // Single-signature sports: pre-fill music, affirmations, defaults,
    // and hand off to the duration picker (step 3).
    setPremiumMusicTrack({ id: `signature_${signatureData.id}`, name: signatureData.name, url: signatureData.audio_url });
    setSubliminalMode(true);
    subliminalModeRef.current = true;
    if (!duration) setDuration("10min");
    try {
      // Most single-signature sports still have at least one focus
      // category in the affirmations DB — pick the first one.
      const focusList = signatureData.focus_areas || [];
      const fallbackFocus = focusList[0]?.id || "confidence";
      setSelectedFocusArea(fallbackFocus);
      await loadSuggestedAffirmations(selectedActivity, fallbackFocus);
    } catch (e) {
      console.warn("[Signature] single-signature affirmation prefill failed", e);
    }
    setStep(3);
  };

  // Handle Signature Focus Area selection.
  // Old behavior: jumped straight to step 4.5 which played JUST the
  // music file (no affirmations, no duration choice, no subliminal
  // overlay). User feedback Feb 26 2026 from a Gym Signature session:
  // "Seems like there are no affirmations, only lasts 2 mins, I can
  // still hear the subliminals." Root cause: signature mode bypassed
  // the entire affirmation player + duration picker + subliminal proxy.
  //
  // New behavior: signature mode now routes through the same full
  // session flow as Custom mode. We pre-fill the music bed with the
  // signature audio URL, load the activity+focus affirmations, and
  // hand off to the duration picker (step 3). The user picks 3/5/10/
  // 20/custom/continuous, the session generates with subliminal mode
  // ON by default, and the player loops the music until the chosen
  // duration is reached.
  const handleSignatureFocusSelect = async (focusArea, withRitual = false) => {
    if (!focusArea.has_audio) return;
    setSelectedFocusAreaName(focusArea.name);
    setSelectedFocusArea(focusArea.id);
    // Lock the music bed to the signature audio (overrides server-side
    // ATMOSPHERE_TO_FILE picker via the new ambient_audio_url field).
    setPremiumMusicTrack({ id: `signature_${focusArea.id}`, name: focusArea.name, url: focusArea.audio_url });
    // Default to subliminal mode for signature sessions — the whole
    // point is barely-audible installation under the music bed.
    setSubliminalMode(true);
    subliminalModeRef.current = true;
    // Default to 10-minute session unless user changes it.
    if (!duration) setDuration("10min");
    // Load the activity+focus affirmations into the editable list so
    // the standard generate-session pathway picks them up.
    try {
      await loadSuggestedAffirmations(selectedActivity, focusArea.id);
    } catch (e) {
      console.warn("[Signature] failed to load suggested affirmations", e);
    }
    if (withRitual && selectedActivity) {
      // Show the pre-performance ritual first, then session
      loadRitualData(selectedActivity);
      setRitualEnabled(true);
      setStep(3.5);
    } else {
      // Skip ritual — go straight to duration/music picker (step 3).
      setStep(3);
    }
  };

  // Handle Custom Mode selection — continue existing flow
  const handleCustomSelect = () => {
    setSignatureMode(false);
    setSelectedFocusAreaName(null);
    setStep(2); // Go to focus area selection
  };

  // Handle focus area selection - go to affirmations (performance)
  const handleFocusAreaSelect = (focusAreaId) => {
    setSelectedFocusArea(focusAreaId);
    loadSuggestedAffirmations(selectedActivity, focusAreaId);
    setStep(2.5); // Go to affirmations editing step
  };

  // Handle affirmations confirmed - go to ritual toggle (performance) or voice selection (other modes)
  const handleAffirmationsConfirmed = () => {
    if (mode === "performance" && selectedActivity) {
      // Load ritual data for the selected sport (only if activity was selected via normal flow)
      loadRitualData(selectedActivity);
      setStep(2.6); // Go to ritual toggle (performance only)
    } else {
      setStep(2.7); // Go to voice selection for other modes or alignment check flow
    }
  };

  // Load pre-performance ritual data
  const loadRitualData = async (sport) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/private-suite/ritual/${sport}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setRitualData(response.data.ritual);
    } catch (err) {
      console.error("Error loading ritual data:", err);
      // Default to general ritual if sport-specific fails
      setRitualData(null);
    }
  };

  // Handle ritual toggle confirmed - go to voice selection
  const handleRitualToggleConfirmed = () => {
    setStep(2.7); // Go to voice selection
  };

  // Handle voice selection confirmed - go to music/duration
  const handleVoiceConfirmed = () => {
    if (voiceOption === "record") {
      setStep(2.8); // Go to recording screen
    } else if (voiceOption === "clone") {
      // Generate with AI voice clone and go directly to duration
      generateWithAiVoice();
    } else {
      setStep(3); // Go to music & duration selection
    }
  };

  // Recording functions for "Record Own Voice"
  const startRecording = async () => {
    try {
      setRecordingError(null);
      setCurrentReadingIndex(0);
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const errMsg = "Your browser doesn't support audio recording. Please use the Reclaim Voice option.";
        setRecordingError(errMsg);
        toast.error(errMsg);
        return;
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else {
          mimeType = '';
        }
      }
      
      const options = mimeType ? { mimeType } : {};
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const actualMimeType = mediaRecorderRef.current.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: actualMimeType });
        setRecordingBlob(blob);
        setRecordingUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start(100);
      setIsRecording(true);
      toast.success("Recording started!");
    } catch (err) {
      let errMsg = "Could not access microphone. Please use the Reclaim Voice option.";
      if (err.name === 'NotAllowedError') {
        errMsg = "Microphone access was blocked. Please allow microphone access or use the Reclaim Voice option.";
      }
      setRecordingError(errMsg);
      toast.error(errMsg);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success("Recording saved!");
    }
  };

  const toggleRecordingPlayback = () => {
    if (recordingPlaybackRef.current) {
      if (isPlayingRecording) {
        recordingPlaybackRef.current.pause();
      } else {
        recordingPlaybackRef.current.play();
      }
      setIsPlayingRecording(!isPlayingRecording);
    }
  };

  const clearRecording = () => {
    setRecordingBlob(null);
    setRecordingUrl(null);
    setCurrentReadingIndex(0);
    if (recordingPlaybackRef.current) {
      recordingPlaybackRef.current.pause();
    }
    setIsPlayingRecording(false);
  };

  // Auto-advance highlighted affirmation while recording
  useEffect(() => {
    let interval;
    const enabledAffirmations = editableAffirmations.filter(a => a.enabled && a.text?.trim());
    if (isRecording && enabledAffirmations.length > 0) {
      interval = setInterval(() => {
        setCurrentReadingIndex(prev => 
          prev < enabledAffirmations.length - 1 ? prev + 1 : prev
        );
      }, 4000); // 4 seconds per affirmation
    }
    return () => clearInterval(interval);
  }, [isRecording, editableAffirmations]);

  // Fetch AI Voice Usage stats
  const fetchAiVoiceUsage = async () => {
    setAiVoiceLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/voice-studio/ai-voice-usage`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setAiVoiceUsage(response.data);
    } catch (err) {
      console.error("Error fetching AI voice usage:", err);
      setAiVoiceUsage({ has_access: false, message: "Unable to check AI voice access" });
    } finally {
      setAiVoiceLoading(false);
    }
  };

  // Fetch AI voice usage when entering voice selection step
  useEffect(() => {
    if (step === 2.7 && !aiVoiceUsage) {
      fetchAiVoiceUsage();
    }
  }, [step]);

  // Generate affirmations using AI clone voice
  const generateWithAiVoice = async () => {
    if (!aiVoiceUsage?.can_generate) {
      toast.error(aiVoiceUsage?.message || "AI voice generation not available");
      return;
    }

    setGeneratingAiVoice(true);
    try {
      const enabledAffirmations = editableAffirmations
        .filter(a => a.enabled && a.text?.trim())
        .map(a => a.text.trim());

      if (enabledAffirmations.length === 0) {
        toast.error("No affirmations to generate");
        return;
      }

      // Generate using the user's cloned voice
      const response = await axios.post(
        `${API_URL}/api/voice-studio/generate`,
        {
          affirmation_text: enabledAffirmations.join("\n\n"),
          voice_style: "calm",
          background_audio: "silence",
          title: `Private Suite - ${mode}`,
          save_to_library: false
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      if (response.data?.audio_url) {
        const audioUrl = response.data.audio_url.startsWith('/')
          ? `${API_URL}${response.data.audio_url}`
          : response.data.audio_url;
        setRecordingUrl(audioUrl);
        toast.success("AI voice generated!");
        
        // Refresh usage stats
        fetchAiVoiceUsage();
        
        // Go to step 3 (duration)
        setStep(3);
      }
    } catch (err) {
      console.error("Error generating AI voice:", err);
      const errorMsg = err.response?.data?.detail || "Failed to generate AI voice";
      toast.error(errorMsg);
    } finally {
      setGeneratingAiVoice(false);
    }
  };

  // Play AI voice demo/preview
  const playAiVoiceDemo = async () => {
    // Stop if already playing
    if (playingAiDemo && aiDemoAudioRef.current) {
      aiDemoAudioRef.current.pause();
      aiDemoAudioRef.current.currentTime = 0;
      setPlayingAiDemo(false);
      return;
    }

    try {
      // Get demo or preview URL based on access
      const endpoint = aiVoiceUsage?.has_access 
        ? `${API_URL}/api/voice-studio/ai-voice-preview`
        : `${API_URL}/api/voice-studio/ai-voice-demo`;
      
      const response = await axios.get(endpoint, {
        headers: aiVoiceUsage?.has_access ? { Authorization: `Bearer ${getToken()}` } : {}
      });

      const audioUrl = response.data.preview_url || response.data.demo_url;
      if (audioUrl) {
        const fullUrl = audioUrl.startsWith('/') ? `${API_URL}${audioUrl}` : audioUrl;
        
        if (!aiDemoAudioRef.current) {
          aiDemoAudioRef.current = new Audio();
        }
        aiDemoAudioRef.current.src = fullUrl;
        aiDemoAudioRef.current.onended = () => setPlayingAiDemo(false);
        aiDemoAudioRef.current.play();
        setPlayingAiDemo(true);
      }
    } catch (err) {
      console.error("Error playing AI voice demo:", err);
      toast.error("Could not play demo");
    }
  };

  // Handle recovery experience selection - go directly to duration
  const handleRecoveryExperienceSelect = async (experience) => {
    setSelectedRecoveryExperience(experience);
    // Load affirmations for this recovery experience
    try {
      const response = await axios.get(
        `${API_URL}/api/private-suite/recovery/affirmations/${experience}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setEditableAffirmations(response.data.affirmations.map(aff => ({
        text: typeof aff === 'string' ? aff : (aff.text || String(aff)),
        enabled: true
      })));
    } catch (err) {
      console.error("Error loading recovery affirmations:", err);
      setEditableAffirmations([]);
    }
    setStep(2.5); // Go to affirmation editing (same as performance)
  };

  // Submit post-session reflection
  const submitReflection = async (feeling) => {
    setPostSessionFeeling(feeling);
    
    if (sessionData?.session_id) {
      try {
        await axios.post(
          `${API_URL}/api/private-suite/reflection/${sessionData.session_id}`,
          { post_feeling: feeling },
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
      } catch (err) {
        console.error("Error saving reflection:", err);
      }
    }
    
    // Move to completion after a gentle pause
    setTimeout(() => {
      setSessionPhase("complete");
    }, 300);
  };

  const generateSession = async () => {
    setLoading(true);
    try {
      // Resolve "custom" to the actual `Nmin` slug the backend expects.
      // The custom input is constrained to 1–60 minutes client-side.
      const resolvedDuration =
        duration === "custom"
          ? `${Math.max(1, Math.min(60, parseInt(customDurationMinutes, 10) || 10))}min`
          : duration;
      // Build request based on mode
      const requestData = {
        mode: mode,
        duration: resolvedDuration,
        subliminal_mode: subliminalMode,
        voice_option: voiceOption,
        affirmation_source: affirmationSource,
      };
      
      if (mode === "meditation") {
        requestData.current_state = currentState;
        requestData.desired_state = desiredState;
        requestData.atmosphere = atmosphere;
        requestData.brain_state = brainState;
        // Pass custom affirmations (only enabled ones with text)
        requestData.custom_affirmations = editableAffirmations
          .filter(aff => aff.enabled && aff.text && typeof aff.text === 'string' && aff.text.trim())
          .map(aff => aff.text.trim());
      } else if (mode === "performance") {
        requestData.activity = selectedActivity;
        requestData.focus_area = selectedFocusArea;
        requestData.music_style = musicStyle;
        // Pass custom affirmations (only enabled ones with text)
        requestData.custom_affirmations = editableAffirmations
          .filter(aff => aff.enabled && aff.text && typeof aff.text === 'string' && aff.text.trim())
          .map(aff => aff.text.trim());
        requestData.skip_intro = true; // Performance mode skips intro
        // Signature Mode override — pin the music bed to the curated
        // signature track (e.g. gym_signature.mp3) instead of letting
        // the backend pick from ATMOSPHERE_TO_FILE.
        if (signatureMode && premiumMusicTrack?.url) {
          requestData.ambient_audio_url = premiumMusicTrack.url;
        }
      } else if (mode === "deep_recovery") {
        requestData.recovery_experience = selectedRecoveryExperience;
        // Pass custom affirmations (only enabled ones with text)
        requestData.custom_affirmations = editableAffirmations
          .filter(aff => aff.enabled && aff.text && typeof aff.text === 'string' && aff.text.trim())
          .map(aff => aff.text.trim());
      } else if (mode === "health_recovery") {
        requestData.health_condition = healthCondition;
        // Pass custom affirmations (only enabled ones with text)
        requestData.custom_affirmations = editableAffirmations
          .filter(aff => aff.enabled && aff.text && typeof aff.text === 'string' && aff.text.trim())
          .map(aff => aff.text.trim());
      }
      
      const response = await axios.post(
        `${API_URL}/api/private-suite/generate-session`,
        requestData,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      let sessionResponse = response.data;
      
      // If user recorded their own voice, use the recording as the single affirmation
      if (voiceOption === "record" && recordingUrl) {
        console.log("[Session] Using user's recorded voice for affirmations");
        sessionResponse = {
          ...sessionResponse,
          affirmations: [{
            text: "Your recorded affirmations",
            audio_url: recordingUrl, // Use the blob URL directly
            is_user_recording: true
          }],
          skip_intro: true // Skip intro for recorded voice sessions
        };
      }
      
      setSessionData(sessionResponse);
      
      // Check if we should show the ritual first (Performance mode only)
      if (mode === "performance" && ritualEnabled && ritualData) {
        setStep(3.5); // Go to ritual player
      } else {
        setStep(4); // Go directly to session
      }
    } catch (err) {
      console.error("Error generating session:", err);
      toast.error("Failed to generate session");
      setBeginExpanding(false); // unblock the Begin dot if generation failed
    } finally {
      setLoading(false);
    }
  };

  // Handle ritual completion - proceed to main session
  const handleRitualComplete = () => {
    setShowingRitual(false);
    setStep(4); // Now start the main session
  };

  // Handle ritual skip - go directly to session
  const handleRitualSkip = () => {
    setShowingRitual(false);
    setStep(4); // Skip ritual, go to session
  };

  const startSession = () => {
    // Track session start for smart suggestions
    trackActivity('suite_session_started', { mode });
    // Auto-tick Daily Essentials · Daily Audio (session-scoped dedupe).
    trackAudioPlayed();
    
    // CRITICAL: Stop any previously playing audio before starting new session
    // This prevents overlapping audio when quickly restarting sessions
    if (introAudioRef.current) introAudioRef.current.pause();
    if (ambientAudioRef.current) ambientAudioRef.current.pause();
    if (affirmationAudioRef.current) affirmationAudioRef.current.pause();
    if (voiceTrackAudioRef.current) voiceTrackAudioRef.current.pause();
    if (breathingAudioRef.current) {
      breathingAudioRef.current.pause();
      breathingAudioRef.current = null;
    }
    
    setIsPlaying(true);
    isPlayingRef.current = true;  // Set ref immediately for sync access
    sessionStartTimeRef.current = Date.now();
    elapsedTimeRef.current = 0;
    setElapsedTime(0);
    
    // Start elapsed timer for all modes
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => {
        elapsedTimeRef.current = prev + 1;
        return prev + 1;
      });
    }, 1000);
    
    // Start ambient audio with appropriate volume for all modes
    if (ambientAudioRef.current) {
      // Premium music picker takes priority over the generated/session ambient.
      if (premiumMusicTrack?.url) {
        const src = premiumMusicTrack.url.startsWith('http')
          ? premiumMusicTrack.url
          : `${API_URL}${premiumMusicTrack.url}`;
        ambientAudioRef.current.src = src;
        console.log("[Session] Using premium music track:", premiumMusicTrack.displayName);
      } else if (sessionData?.ambient_audio_url) {
        ambientAudioRef.current.src = `${API_URL}${sessionData.ambient_audio_url}`;
        console.log("[Session] Using static ambient audio");
      }
      ambientAudioRef.current.volume = subliminalMode ? 0.8 : 0.4;
      // Preload and start music immediately
      ambientAudioRef.current.load();
      ambientAudioRef.current.play().catch(err => {
        console.log("[Session] Music autoplay blocked, will retry:", err);
        // Retry after a short delay (helps with autoplay policies)
        setTimeout(() => {
          ambientAudioRef.current?.play().catch(console.error);
        }, 500);
      });
    }

    // Binaural beats layer — plays UNDER everything at low volume when on.
    if (binauralAudioRef.current && binauralEnabled && binauralBeat?.url) {
      const src = binauralBeat.url.startsWith('http')
        ? binauralBeat.url
        : `${API_URL}${binauralBeat.url}`;
      binauralAudioRef.current.src = src;
      binauralAudioRef.current.volume = binauralVolume;
      binauralAudioRef.current.load();
      binauralAudioRef.current.play().catch(() => {
        setTimeout(() => binauralAudioRef.current?.play().catch(() => {}), 500);
      });
    }
    
    // Performance mode and Health Recovery skip intro - go straight to affirmations/music
    if (mode === "performance" || mode === "health_recovery" || sessionData?.skip_intro) {
      setSessionPhase("affirmations");
      startAffirmations();
      return;
    }
    
    // Normal flow: intro -> breathing -> affirmations
    setSessionPhase("intro");
    
    // Play intro audio if available
    if (sessionData?.intro_audio_url && introAudioRef.current) {
      introAudioRef.current.src = `${API_URL}${sessionData.intro_audio_url}`;
      
      introAudioRef.current.onended = () => {
        startBreathing();
      };
      
      introAudioRef.current.play().catch(err => {
        console.log("[Session] Intro audio blocked, proceeding after delay:", err);
        // If autoplay is blocked, still proceed after a short pause
        setTimeout(() => startBreathing(), 3000);
      });
    } else {
      // If no intro audio, start breathing after a pause
      setTimeout(() => startBreathing(), 3000);
    }
  };
  
  // Keep startSession accessible via ref for alignment check auto-start
  useEffect(() => {
    startSessionRef.current = startSession;
  });

  const startBreathing = () => {
    setSessionPhase("breathing");
    setBreathPhase("inhale");
    setBreathCount(0);
    
    // Stop intro audio if still playing (safety measure for overlapping audio)
    if (introAudioRef.current) {
      introAudioRef.current.pause();
    }
    
    // Play the appropriate breathing audio based on cycle count
    const totalCycles = sessionData?.breathing_config?.cycles || 3;
    const audioUrl = totalCycles === 2 ? TWO_BREATHS_AUDIO : THREE_BREATHS_AUDIO;
    
    if (breathingAudioRef.current) {
      breathingAudioRef.current.pause();
    }
    breathingAudioRef.current = new Audio(audioUrl);
    breathingAudioRef.current.volume = voiceVolume(getStoredAudioMode());
    breathingAudioRef.current.play().catch(err => console.log("Breathing audio failed:", err));
    
    runBreathingCycle(0, 0);
  };

  const runBreathingCycle = (cycle, phase) => {
    const phases = ["inhale", "hold", "release", "rest"];
    const phaseDuration = sessionData?.breathing_config?.phase_duration || 7000;
    const totalCycles = sessionData?.breathing_config?.cycles || 3;
    
    if (cycle >= totalCycles) {
      // Breathing complete, move to affirmations
      startAffirmations();
      return;
    }
    
    setBreathPhase(phases[phase]);
    
    breathTimerRef.current = setTimeout(() => {
      const nextPhase = (phase + 1) % 4;
      const nextCycle = nextPhase === 0 ? cycle + 1 : cycle;
      
      if (nextPhase === 0) {
        setBreathCount(nextCycle);
      }
      
      runBreathingCycle(nextCycle, nextPhase);
    }, phaseDuration);
  };

  const startAffirmations = () => {
    setSessionPhase("affirmations");
    setBreathPhase(null);
    
    // Stop breathing audio if playing
    if (breathingAudioRef.current) {
      breathingAudioRef.current.pause();
      breathingAudioRef.current = null;
    }
    
    // Stop intro audio if still playing (safety measure for overlapping audio)
    if (introAudioRef.current) {
      introAudioRef.current.pause();
    }
    
    // For Performance mode: use individual split affirmation audio files
    // (not the combined sport_affirmations track which contains ALL focus areas and the title)
    // The sessionData.affirmations already contains the correct files filtered by focus_area
    if (mode === "performance" && sessionData?.affirmations?.length > 0) {
      console.log("[VoiceTrack] Performance mode: using individual split affirmation files");
      playAffirmation(0);
      return;
    }
    
    // For Health Recovery with user recording: play recording once then loop ambient
    if ((mode === "health_recovery" || voiceOption === "record") && sessionData?.affirmations?.[0]?.is_user_recording) {
      console.log("[VoiceTrack] User recording mode: playing recorded affirmations");
      playAffirmation(0);
      return;
    }
    
    // For other modes, play ElevenLabs-generated affirmations
    if (sessionData?.affirmations?.length > 0) {
      playAffirmation(0);
    } else {
      // No affirmations, move to ambient
      setTimeout(() => startAmbient(), 2000);
    }
  };

  const playAffirmation = (index) => {
    const affirmations = sessionData?.affirmations || [];
    
    // Check if session is still playing using ref (avoids stale closure)
    if (!isPlayingRef.current) {
      console.log("[Affirmation] Session not playing, stopping");
      return;
    }
    
    // Check if session should have ended (skip for continuous mode)
    const totalDuration = sessionData?.duration_seconds;
    const isContinuous = !totalDuration || totalDuration === 0;
    
    if (!isContinuous && elapsedTimeRef.current >= totalDuration) {
      console.log("[Affirmation] Session time exceeded, completing");
      startAmbient(); // Will trigger completion
      return;
    }
    
    if (index >= affirmations.length) {
      // Loop back to start - affirmations play continuously throughout session
      console.log("[Affirmation] Completed all, looping back to start");
      setTimeout(() => playAffirmation(0), 4000);
      return;
    }
    
    const aff = affirmations[index];
    console.log(`[Affirmation] Playing ${index + 1}/${affirmations.length}: ${aff.text?.substring(0, 30)}...`);
    
    // Update current affirmation text for display
    setCurrentAffirmationText(aff.text);
    
    if (aff.audio_url && affirmationAudioRef.current) {
      // Subliminal mode: route every affirmation URL through the
      // server-side `/api/private-suite/subliminal-proxy` endpoint,
      // which loads the source file and re-encodes it -28dB quieter.
      // The file ITSELF arrives quiet — we don't depend on the iOS
      // `audioElement.volume` API which is unreliable on Bluetooth/
      // AirPlay (was clamping our 0.015 setting to perceivable
      // levels). When subliminal is OFF we serve the original file
      // at element volume 0.6 (voice-forward).
      const isSubliminal = subliminalModeRef.current;
      
      // Handle user recordings (blob URLs) vs server URLs
      let audioUrl;
      if (aff.is_user_recording || aff.audio_url.startsWith("blob:")) {
        audioUrl = aff.audio_url;
        console.log("[Affirmation] Using user's recorded audio");
      } else {
        const rawPath = aff.audio_url.startsWith("http")
          ? new URL(aff.audio_url).pathname
          : aff.audio_url;
        if (isSubliminal && rawPath.startsWith("/api/")) {
          // Route through the subliminal proxy (server bakes -28dB
          // into the served file). Works uniformly across every
          // mount: static affirmations, voice packs, dynamic endpoint.
          audioUrl = `${API_URL}/api/private-suite/subliminal-proxy?src=${encodeURIComponent(rawPath)}`;
        } else {
          audioUrl = aff.audio_url.startsWith("http")
            ? aff.audio_url
            : `${API_URL}${aff.audio_url}`;
        }
      }
      
      const audioEl = affirmationAudioRef.current;
      audioEl.src = audioUrl;
      
      // Clean up previous handlers
      audioEl.onended = null;
      audioEl.onerror = null;
      audioEl.oncanplaythrough = null;
      
      // Track if we've already moved to next (prevent double-triggering)
      let hasAdvanced = false;
      const advanceToNext = () => {
        if (hasAdvanced) return;
        hasAdvanced = true;
        setTimeout(() => setCurrentAffirmationText(null), 1000);
        // Longer pause between affirmations for better spacing
        // Performance mode: 4 seconds, other modes: 6 seconds for calmer pacing
        const pauseDuration = mode === "performance" ? 4000 : 6000;
        setTimeout(() => playAffirmation(index + 1), pauseDuration);
      };
      
      audioEl.onended = () => {
        console.log(`[Affirmation] ${index + 1} ended naturally`);
        advanceToNext();
      };
      
      audioEl.onerror = (e) => {
        console.error(`[Affirmation] ${index + 1} error:`, e);
        advanceToNext();
      };
      
      // Element volume:
      //   subliminal=ON  → 1.0 (the file is already -28dB quieter, so
      //     playing at full element volume gives the final calibrated
      //     whisper level. iOS Bluetooth quirks are irrelevant — the
      //     bytes themselves are quiet).
      //   subliminal=OFF → 0.6 (voice-forward over music bed).
      const volume = isSubliminal ? 1.0 : 0.6;
      audioEl.volume = volume;
      console.log(`[Affirmation] Volume ${volume.toFixed(2)} (subliminal: ${isSubliminal})`);
      
      // Load and play
      audioEl.load();
      const playPromise = audioEl.play();
      
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          console.error(`[Affirmation] ${index + 1} play failed:`, err);
          advanceToNext();
        });
      }
    } else {
      console.log(`[Affirmation] ${index + 1} has no audio, skipping`);
      setTimeout(() => playAffirmation(index + 1), 500);
    }
  };

  const startAmbient = () => {
    // Skip setting phase to "ambient" - we're already playing affirmations continuously
    // This function now just handles session completion timing
    
    // Use ref for accurate elapsed time (avoids stale closure issue)
    const totalDuration = sessionData?.duration_seconds;
    const isContinuous = !totalDuration || totalDuration === 0;
    
    // In continuous mode, don't auto-complete - user must stop manually
    if (isContinuous) {
      console.log(`[Private Suite] Continuous mode - session will loop until stopped`);
      return;
    }
    
    const currentElapsed = elapsedTimeRef.current;
    const remainingTime = totalDuration - currentElapsed;
    
    console.log(`[Private Suite] Checking session timing. Elapsed: ${currentElapsed}s, Duration: ${totalDuration}s, Remaining: ${remainingTime}s`);
    
    if (remainingTime > 0) {
      setTimeout(() => completeSession(), remainingTime * 1000);
    } else {
      // Session has already exceeded duration, complete immediately
      completeSession();
    }
  };

  const completeSession = async () => {
    // Show reflection first, then complete
    setSessionPhase("reflection");
    setIsPlaying(false);
    isPlayingRef.current = false;
    
    // Stop all audio with gentle fade
    if (introAudioRef.current) introAudioRef.current.pause();
    if (breathingAudioRef.current) breathingAudioRef.current.pause();
    if (voiceTrackAudioRef.current) voiceTrackAudioRef.current.pause();  // Stop voice tracks
    if (ambientAudioRef.current) {
      // Gentle fade out ambient
      let volume = ambientAudioRef.current.volume;
      const fadeInterval = setInterval(() => {
        volume -= 0.03;
        if (volume <= 0) {
          ambientAudioRef.current.pause();
          clearInterval(fadeInterval);
        } else {
          ambientAudioRef.current.volume = volume;
        }
      }, 100);
    }
    if (affirmationAudioRef.current) affirmationAudioRef.current.pause();
    
    // Clear timers
    if (timerRef.current) clearInterval(timerRef.current);
    if (breathTimerRef.current) clearTimeout(breathTimerRef.current);
    
    // Mark session complete and check for milestones
    if (sessionData?.session_id) {
      try {
        const response = await axios.post(
          `${API_URL}/api/private-suite/complete/${sessionData.session_id}`,
          {},
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        
        // Check if user reached a new milestone
        if (response.data.new_milestone) {
          setNewMilestone(response.data.new_milestone);
          setTotalMinutesForMilestone(response.data.total_minutes);
          // Show celebration after a brief moment (let reflection complete first)
          setTimeout(() => {
            setShowMilestoneCelebration(true);
          }, 500);
        }
        
        // Show "Save as State" prompt for first-time completions
        const hasSeenSaveState = localStorage.getItem('privateSuiteSaveStateShown');
        if (!hasSeenSaveState && desiredState) {
          setTimeout(() => {
            setShowSaveState(true);
            localStorage.setItem('privateSuiteSaveStateShown', 'true');
          }, 1500);
        }
      } catch (err) {
        console.error("Error completing session:", err);
      }
    }
  };

  const pauseSession = () => {
    setIsPlaying(false);
    isPlayingRef.current = false;
    if (introAudioRef.current) introAudioRef.current.pause();
    if (breathingAudioRef.current) breathingAudioRef.current.pause();
    if (ambientAudioRef.current) ambientAudioRef.current.pause();
    if (affirmationAudioRef.current) affirmationAudioRef.current.pause();
    if (voiceTrackAudioRef.current) voiceTrackAudioRef.current.pause();  // Stop voice tracks
    if (binauralAudioRef.current) binauralAudioRef.current.pause();
    if (timerRef.current) clearInterval(timerRef.current);
    if (breathTimerRef.current) clearTimeout(breathTimerRef.current);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (introAudioRef.current) introAudioRef.current.muted = !isMuted;
    if (breathingAudioRef.current) breathingAudioRef.current.muted = !isMuted;
    if (ambientAudioRef.current) ambientAudioRef.current.muted = !isMuted;
    if (affirmationAudioRef.current) affirmationAudioRef.current.muted = !isMuted;
    if (binauralAudioRef.current) binauralAudioRef.current.muted = !isMuted;
  };

  const formatTime = (seconds) => {
    // Return infinity symbol for continuous mode (null/undefined duration)
    if (seconds === null || seconds === undefined) return "∞";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    // Copy refs at effect start to use in cleanup
    const introAudio = introAudioRef.current;
    const ambientAudio = ambientAudioRef.current;
    const affirmationAudio = affirmationAudioRef.current;
    const voiceTrackAudio = voiceTrackAudioRef.current;
    const binauralAudio = binauralAudioRef.current;
    const timer = timerRef.current;
    const breathTimer = breathTimerRef.current;
    
    return () => {
      // Cleanup all timers
      if (timer) clearInterval(timer);
      if (breathTimer) clearTimeout(breathTimer);
      
      // CRITICAL: Stop all audio on component unmount to prevent overlapping audio
      if (introAudio) {
        introAudio.pause();
        introAudio.src = "";
      }
      if (ambientAudio) {
        ambientAudio.pause();
        ambientAudio.src = "";
      }
      if (affirmationAudio) {
        affirmationAudio.pause();
        affirmationAudio.src = "";
      }
      if (voiceTrackAudio) {
        voiceTrackAudio.pause();
        voiceTrackAudio.src = "";
      }
      if (binauralAudio) {
        binauralAudio.pause();
        binauralAudio.src = "";
      }
      if (breathingAudioRef.current) {
        breathingAudioRef.current.pause();
        breathingAudioRef.current = null;
      }
    };
  }, []);

  // Page transition variants
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.3 } }
  };

  const staggerContainer = {
    animate: { transition: { staggerChildren: 0.08 } }
  };

  const itemVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.4 } }
  };

  // Loading state - show special message when coming from alignment check
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a1a2e] to-[#16213e] flex flex-col items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white/80 rounded-full animate-spin mb-4" />
        {fromAlignmentCheck && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-white/60 text-sm"
          >
            Preparing your personalised session...
          </motion.p>
        )}
      </div>
    );
  }

  // Premium gate - allow preview access for users coming from Alignment Check
  if (!hasAccess && !fromAlignmentCheck && step === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a1a2e] to-[#16213e] flex flex-col">
        <div className="p-6">
          <button onClick={() => navigate(-1)} className="text-white/60 hover:text-white">
            <ArrowLeft size={24} />
          </button>
        </div>
        
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="w-20 h-20 bg-gradient-to-br from-amber-400/20 to-orange-400/20 rounded-full flex items-center justify-center mb-8"
          >
            <Lock size={36} className="text-amber-400" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-heading text-3xl text-white mb-4"
          >
            Private Suite
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-white/60 text-lg mb-8 max-w-sm"
          >
            Upgrade to Premium to access your Private Suite
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Button
              onClick={() => navigate("/pricing")}
              className="bg-gradient-to-r from-amber-400 to-orange-400 text-black font-medium px-8 py-3 rounded-full"
            >
              View Premium
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col pb-20"
      style={{
        background: "linear-gradient(180deg, #1f3a5c 0%, #0c1a30 55%, #03060f 100%)",
      }}
    >
      {/* Hidden audio elements */}
      <audio ref={introAudioRef} />
      <audio ref={ambientAudioRef} loop />
      <audio ref={affirmationAudioRef} />
      <audio ref={voiceTrackAudioRef} />
      <audio ref={binauralAudioRef} loop />

      {/* Premium music picker overlay — Collective Alignment aesthetic */}
      {showMusicPicker && (
        <MusicSelection
          accent="gold"
          onBack={() => setShowMusicPicker(false)}
          onSelect={(track) => {
            setPremiumMusicTrack(track);
            // Keep the legacy `musicStyle` string roughly synced for the
            // generation request payload (the backend still expects a
            // category id — we map to the track's id which now uniquely
            // identifies the chosen file).
            setMusicStyle(track.id);
            setShowMusicPicker(false);
          }}
        />
      )}

      {/* Binaural Beats picker — 5 bands × 2 variants */}
      <AnimatePresence>
        {showBinauralPicker && (
          <BinauralPicker
            current={binauralBeat}
            onClose={() => setShowBinauralPicker(false)}
            onSelect={(beat) => {
              setBinauralBeat(beat);
              setShowBinauralPicker(false);
            }}
          />
        )}
      </AnimatePresence>
      
      <AnimatePresence mode="wait">
        {/* Step 0: Entry Screen */}
        {step === 0 && (
          <motion.div
            key="entry"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="flex-1 flex flex-col"
          >
            <div className="p-6">
              <button onClick={() => navigate(-1)} className="text-white/60 hover:text-white" data-testid="ps-back-btn">
                <ArrowLeft size={24} />
              </button>
            </div>

            {/* ─── D3.54 (Jun 2026) — Editorial entry, Inner-Path styled ─── */}
            <div className="flex-1 px-6 pb-10 max-w-3xl mx-auto w-full">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15, duration: 0.6 }}
                className="uppercase tracking-[0.4em] mb-4"
                style={{ color: "#D7BB7A", fontFamily: '"Manrope", sans-serif', fontSize: "0.6rem" }}
                data-testid="ps-eyebrow"
              >
                Private Suite
              </motion.p>

              <motion.h1
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.22, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="mb-3"
                style={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontSize: "clamp(2.2rem, 5.2vw, 3.2rem)",
                  fontWeight: 300,
                  color: "#F3EDDD",
                  letterSpacing: "0.01em",
                  lineHeight: 1.15,
                }}
              >
                Your Private Suite Layer
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.32, duration: 0.6 }}
                className="italic mb-10"
                style={{
                  color: "rgba(243, 237, 221, 0.55)",
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: "clamp(1rem, 2.4vw, 1.18rem)",
                  lineHeight: 1.55,
                  maxWidth: "32rem",
                }}
              >
                Your state. Your focus. Your direction.<br />
                Where everything becomes personal.
              </motion.p>

              {/* Today's Centre — visual anchor, like Inner Path's state card */}
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-2xl p-6 mb-10 relative overflow-hidden"
                style={{
                  background: "linear-gradient(155deg, rgba(13, 36, 56, 0.85) 0%, rgba(10, 24, 40, 0.92) 100%)",
                  border: "1px solid rgba(215, 187, 122, 0.14)",
                }}
                data-testid="ps-state-card"
              >
                <p
                  className="uppercase tracking-[0.32em] mb-2"
                  style={{ color: "#D7BB7A", fontFamily: '"Manrope", sans-serif', fontSize: "0.58rem", opacity: 0.78 }}
                >
                  Your current state
                </p>
                <p
                  className="mb-1"
                  style={{
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontSize: "clamp(1.8rem, 4.4vw, 2.4rem)",
                    fontWeight: 300,
                    color: "#F3EDDD",
                    lineHeight: 1.15,
                  }}
                >
                  Begin where you are.
                </p>
                <p
                  className="italic"
                  style={{
                    color: "rgba(243, 237, 221, 0.46)",
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "0.95rem",
                    lineHeight: 1.55,
                  }}
                >
                  Today's session — return whenever the day pulls you out.
                </p>

                <motion.button
                  onClick={handleBeginExperience}
                  disabled={isEntering}
                  whileHover={{ y: -1 }}
                  className="rec-chip mt-6"
                  data-active="true"
                  data-testid="begin-private-suite-btn"
                  style={{
                    padding: "0.85rem 1.7rem",
                    fontSize: "0.66rem",
                    opacity: isEntering ? 0 : 1,
                    transition: "opacity 0.4s ease",
                  }}
                >
                  Create Your State →
                </motion.button>
              </motion.div>

              {/* Daily Edge — paths */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="mb-2"
              >
                <p
                  className="uppercase tracking-[0.32em] mb-2"
                  style={{ color: "#D7BB7A", fontFamily: '"Manrope", sans-serif', fontSize: "0.6rem" }}
                >
                  Daily Edge
                </p>
                <p
                  className="italic mb-5"
                  style={{
                    color: "rgba(243, 237, 221, 0.5)",
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "0.95rem",
                  }}
                >
                  Choose your path into the field.
                </p>
              </motion.div>

              {/* Rows — Inner Path-style list */}
              <div className="space-y-3 mb-10">
                <EditorialRow
                  icon={<Sparkles size={18} style={{ color: "#D7BB7A" }} />}
                  title="My Journeys"
                  subtitle="Build your own audio soundtrack — music, binaural, affirmations"
                  onClick={() => navigate("/my-journeys")}
                  testid="my-journeys-btn"
                />
                <EditorialRow
                  icon={<Sparkles size={18} style={{ color: "#D7BB7A" }} />}
                  title="My States & Realities"
                  subtitle="Your saved states · play, re-enter, evolve"
                  onClick={() => setShowMyStates(true)}
                  testid="my-states-btn"
                />
                <EditorialRow
                  icon={<Heart size={18} style={{ color: "#D7BB7A" }} />}
                  title="Gift a Moment"
                  subtitle="Send a personalised affirmation audio to someone you love"
                  onClick={() => navigate("/gift-moment/create")}
                  testid="gift-a-moment-btn"
                />
                <EditorialRow
                  icon={<Leaf size={18} style={{ color: "rgba(140, 190, 140, 0.85)" }} />}
                  title="Health Support"
                  subtitle="Where would you like to bring softness today?"
                  onClick={() => navigate("/healing")}
                  testid="healing-support-entry-btn"
                />
                <EditorialRow
                  icon={<Target size={18} style={{ color: "rgba(140, 200, 140, 0.85)" }} />}
                  title="Enter Performance"
                  subtitle="Sport-specific state conditioning"
                  onClick={handleEnterPerformance}
                  testid="enter-performance-btn"
                />
              </div>

              {/* Quieter footer chips — history + stats */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.6 }}
                className="flex flex-wrap items-center gap-3"
              >
                <button
                  onClick={() => { loadHistory(); setStep(5); }}
                  className="rec-chip"
                  data-testid="view-history-btn"
                  style={{ padding: "0.5rem 0.95rem", fontSize: "0.56rem" }}
                >
                  <History size={12} />
                  Session History
                </button>
                <button
                  onClick={() => navigate("/private-suite/stats")}
                  className="rec-chip"
                  data-testid="view-stats-btn"
                  style={{ padding: "0.5rem 0.95rem", fontSize: "0.56rem" }}
                >
                  <TrendingUp size={12} />
                  Journey & Stats
                </button>
              </motion.div>

              {/* Insights Preview — kept, restyled */}
              {insights?.has_insights && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                  className="mt-10 rounded-2xl p-5"
                  style={{
                    background: "linear-gradient(145deg, rgba(13,36,56,0.7) 0%, rgba(7,13,11,0.85) 100%)",
                    border: "1px solid rgba(215, 187, 122, 0.10)",
                  }}
                  data-testid="ps-insights-card"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp size={13} style={{ color: "#D7BB7A" }} />
                    <span
                      className="uppercase tracking-[0.32em]"
                      style={{ color: "#D7BB7A", fontFamily: '"Manrope", sans-serif', fontSize: "0.55rem" }}
                    >
                      This Week
                    </span>
                  </div>
                  {insights.patterns?.[0] && (
                    <p
                      className="italic"
                      style={{
                        color: "rgba(243, 237, 221, 0.82)",
                        fontFamily: '"Cormorant Garamond", serif',
                        fontSize: "1.05rem",
                        lineHeight: 1.55,
                      }}
                    >
                      {insights.patterns[0].message}
                    </p>
                  )}
                  {insights.total_sessions_this_week > 0 && (
                    <p
                      className="mt-3"
                      style={{
                        color: "rgba(243, 237, 221, 0.38)",
                        fontFamily: '"Manrope", sans-serif',
                        fontSize: "0.7rem",
                        letterSpacing: "0.06em",
                      }}
                    >
                      {insights.total_sessions_this_week} sessions · {insights.total_minutes_this_week} minutes
                    </p>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>
        )}

        {/* Step 0.5: Mode Selection — D3.56 editorial polish */}
        {step === 0.5 && (
          <motion.div
            key="mode-select"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="flex-1 flex flex-col p-6 max-w-3xl mx-auto w-full"
          >
            <EditorialStepHeader
              onBack={() => setStep(0)}
              eyebrow="Choose Your Mode"
              title="What does today ask for?"
              subtitle="Each mode is a different doorway into the field."
              backTestid="mode-back-btn"
            />

            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="space-y-3"
            >
              <motion.div variants={itemVariants}>
                <EditorialSelectCard
                  icon={<Heart size={20} style={{ color: "#D7BB7A" }} />}
                  title="Calm / Meditation"
                  subtitle="Emotional check-in, breathing, affirmations."
                  onClick={() => handleModeSelect("meditation")}
                  testid="mode-meditation"
                />
              </motion.div>
              <motion.div variants={itemVariants}>
                <EditorialSelectCard
                  icon={<Zap size={20} style={{ color: "#D7BB7A" }} />}
                  title="Performance Mode"
                  subtitle="Sports psychology, subliminals, high-energy state."
                  chips={["Gym", "Golf", "Tennis", "Running", "+3"]}
                  onClick={() => handleModeSelect("performance")}
                  testid="mode-performance"
                />
              </motion.div>
              <motion.div variants={itemVariants}>
                <EditorialSelectCard
                  icon={<Shield size={20} style={{ color: "#D7BB7A" }} />}
                  title="Deep Recovery"
                  subtitle="PTSD support, sleep, nervous system reset."
                  onClick={() => handleModeSelect("deep_recovery")}
                  testid="mode-deep-recovery"
                />
              </motion.div>
              <motion.div variants={itemVariants}>
                <EditorialSelectCard
                  icon={<Leaf size={20} style={{ color: "rgba(140, 200, 140, 0.85)" }} />}
                  title="Health Recovery"
                  subtitle="Healing support, body restoration, deep comfort."
                  badge="AI Assistant"
                  badgeColor="#8CC88C"
                  onClick={() => handleModeSelect("health_recovery")}
                  testid="mode-health-recovery"
                />
              </motion.div>
              <motion.div variants={itemVariants}>
                <EditorialSelectCard
                  icon={<Sparkles size={20} style={{ color: "#D7BB7A" }} />}
                  title="Tune In To Reality"
                  subtitle="Step into the version of you that already exists."
                  chips={["Wealthy You", "Rockstar", "Champion"]}
                  onClick={() => handleModeSelect("reality")}
                  testid="mode-reality"
                />
              </motion.div>
            </motion.div>
          </motion.div>
        )}

        {/* Step 1: Context-Aware Selection */}
        {step === 1 && mode === "meditation" && (
          <motion.div
            key="current-state"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="flex-1 flex flex-col p-6"
          >
            <button onClick={() => setStep(0.5)} className="text-white/60 hover:text-white mb-8">
              <ArrowLeft size={24} />
            </button>
            
            <div className="mb-10">
              <p className="text-white/40 text-sm uppercase tracking-[0.2em] mb-3">Step 1 of 4</p>
              <h2 className="font-heading text-3xl text-white">
                What are you feeling<br />right now?
              </h2>
              <p className="text-white/30 text-sm mt-4">
                Notice… are you open or closed?
              </p>
            </div>
            
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="grid grid-cols-2 gap-4"
            >
              {CURRENT_STATES.map((state) => {
                const Icon = state.icon;
                const isSelected = currentState === state.id;
                return (
                  <motion.button
                    key={state.id}
                    variants={itemVariants}
                    onClick={() => {
                      setCurrentState(state.id);
                      setTimeout(() => setStep(2), 300);
                    }}
                    className={`p-6 rounded-2xl transition-all ${
                      isSelected
                        ? `bg-gradient-to-br ${state.color} text-white scale-105`
                        : "bg-white/5 text-white/70 hover:bg-white/10"
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid={`state-${state.id}`}
                  >
                    <Icon size={28} className="mb-3" />
                    <p className="font-medium">{state.label}</p>
                  </motion.button>
                );
              })}
            </motion.div>
            
            {/* Micro feedback after selection */}
            {currentState && (
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-white/40 text-sm text-center mt-6 italic"
              >
                Got it… let's shift that
              </motion.p>
            )}
          </motion.div>
        )}

        {/* Step 1: Activity Selection (Performance Mode) */}
        {step === 1 && mode === "performance" && (
          <motion.div
            key="activity-select"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="flex-1 flex flex-col p-6 max-w-3xl mx-auto w-full"
          >
            <EditorialStepHeader
              onBack={() => setStep(0.5)}
              eyebrow="Performance · Activity"
              title="Select your activity"
              subtitle="Each sport has its own affirmation library and emotional arc."
              backTestid="activity-back-btn"
            />

            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="grid grid-cols-1 sm:grid-cols-2 gap-3"
            >
              {activities.map((activity) => {
                const Icon = ACTIVITY_ICONS[activity.id] || Trophy;
                return (
                  <motion.div key={activity.id} variants={itemVariants}>
                    <EditorialSelectCard
                      icon={<Icon size={20} style={{ color: "#D7BB7A" }} />}
                      title={activity.label}
                      subtitle={activity.description}
                      onClick={() => handleActivitySelect(activity.id)}
                      testid={`activity-${activity.id}`}
                    />
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>
        )}

        {/* Step 1.5: Signature vs Custom Mode (Performance) */}
        {step === 1.5 && mode === "performance" && (
          <SignatureSelector
            sport={selectedActivity}
            sportLabel={activities.find(a => a.id === selectedActivity)?.label || selectedActivity}
            signatureData={signatureData}
            onSelectSignature={handleSignatureSelect}
            onSelectCustom={handleCustomSelect}
            onBack={() => setStep(1)}
          />
        )}

        {/* Step 1.6: Signature Focus Area (e.g. Golf → Driving, Irons, Putting...) */}
        {step === 1.6 && mode === "performance" && signatureData?.has_focus_areas && (
          <SignatureFocusSelector
            sportLabel={activities.find(a => a.id === selectedActivity)?.label || selectedActivity}
            signatureData={signatureData}
            onSelectFocusArea={handleSignatureFocusSelect}
            onBack={() => setStep(1.5)}
          />
        )}

        {/* Step 1: Experience Selection (Deep Recovery Mode) */}
        {step === 1 && mode === "deep_recovery" && (
          <motion.div
            key="recovery-select"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="flex-1 flex flex-col p-6"
          >
            <button onClick={() => setStep(0.5)} className="text-white/60 hover:text-white mb-8">
              <ArrowLeft size={24} />
            </button>
            
            <div className="mb-8">
              <p className="text-slate-400/80 text-sm uppercase tracking-[0.2em] mb-3">Deep Recovery</p>
              <h2 className="font-heading text-3xl text-white">
                What do you need?
              </h2>
              <p className="text-white/40 text-sm mt-2">This is a safe space for healing</p>
            </div>
            
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="space-y-3"
            >
              {recoveryExperiences.map((exp) => {
                const Icon = RECOVERY_ICONS[exp.id] || Shield;
                return (
                  <motion.button
                    key={exp.id}
                    variants={itemVariants}
                    onClick={() => handleRecoveryExperienceSelect(exp.id)}
                    className={`w-full p-5 rounded-2xl bg-gradient-to-br ${exp.color} border border-white/10 text-left hover:border-white/20 transition-all group`}
                    data-testid={`recovery-${exp.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                        <Icon size={22} className="text-white/80" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-white">{exp.label}</p>
                        <p className="text-white/60 text-sm mt-0.5">{exp.description}</p>
                      </div>
                      <ChevronRight className="text-white/30 group-hover:text-white/60 transition-colors" />
                    </div>
                  </motion.button>
                );
              })}
            </motion.div>
          </motion.div>
        )}

        {/* Step 1: Health Recovery - Conversational AI Assistant Flow */}
        {step === 1 && mode === "health_recovery" && (
          <motion.div
            key="health-recovery"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="flex-1 flex flex-col p-6 overflow-y-auto"
          >
            <button onClick={() => {
              if (healthStep > 1) {
                setHealthStep(healthStep - 1);
              } else {
                setStep(0.5);
                setHealthStep(1);
                setHealthFeeling(null);
                setHealthContext(null);
                setHealthDesiredState(null);
                setHealthFreeText("");
              }
            }} className="text-white/60 hover:text-white mb-4">
              <ArrowLeft size={24} />
            </button>
            
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Leaf size={18} className="text-emerald-400" />
                <p className="text-emerald-400/80 text-xs uppercase tracking-[0.2em]">Health Recovery</p>
              </div>
              
              {/* AI Assistant presence */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500/30 to-teal-500/30 flex items-center justify-center">
                  <Sparkles size={16} className="text-emerald-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Reclaim Healing Assistant</p>
                  <p className="text-white/40 text-xs">A calm presence beside you</p>
                </div>
              </div>
            </div>

            {/* Step 1.1: Initial Check-In */}
            {healthStep === 1 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-2xl p-4 mb-4">
                  <p className="text-white/90 text-sm leading-relaxed">
                    "Hello... there is nothing you need to do right now. Let's gently check in with how you're feeling."
                  </p>
                </div>
                
                <h3 className="text-white font-medium">How are you feeling right now?</h3>
                
                <div className="space-y-2">
                  {HEALTH_FEELINGS.map((feeling) => (
                    <button
                      key={feeling.id}
                      onClick={() => setHealthFeeling(feeling.id)}
                      className={`w-full p-4 rounded-xl text-left transition-all flex items-center gap-3 ${
                        healthFeeling === feeling.id
                          ? "bg-emerald-500/20 border border-emerald-500/40"
                          : "bg-white/5 border border-transparent hover:bg-white/10"
                      }`}
                      data-testid={`health-feeling-${feeling.id}`}
                    >
                      <span className="text-xl">{feeling.icon}</span>
                      <span className={healthFeeling === feeling.id ? "text-white" : "text-white/70"}>
                        {feeling.label}
                      </span>
                      {healthFeeling === feeling.id && (
                        <div className="ml-auto w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                          <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                            <path d="M2.5 6L5 8.5L9.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                
                {healthFeeling === "other" && (
                  <textarea
                    value={healthFreeText}
                    onChange={(e) => setHealthFreeText(e.target.value)}
                    placeholder="Tell me more about how you're feeling..."
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white placeholder:text-white/30 text-sm resize-none focus:border-emerald-500/50 focus:outline-none"
                    rows={2}
                  />
                )}
                
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: healthFeeling ? 1 : 0.5 }}
                  onClick={() => setHealthStep(2)}
                  disabled={!healthFeeling}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold mt-4"
                  data-testid="health-continue-1"
                >
                  Continue
                </motion.button>
              </motion.div>
            )}

            {/* Step 1.2: Recovery Context (Optional) */}
            {healthStep === 2 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-2xl p-4 mb-4">
                  <p className="text-white/90 text-sm leading-relaxed">
                    "Thank you for sharing... Is there anything your body is currently recovering from or going through? This helps me personalize your affirmations."
                  </p>
                </div>
                
                <h3 className="text-white font-medium">What is your body experiencing? <span className="text-white/40 font-normal">(optional)</span></h3>
                
                <div className="space-y-2">
                  {HEALTH_CONTEXTS.map((context) => (
                    <button
                      key={context.id}
                      onClick={() => setHealthContext(context.id)}
                      className={`w-full p-4 rounded-xl text-left transition-all ${
                        healthContext === context.id
                          ? "bg-emerald-500/20 border border-emerald-500/40"
                          : "bg-white/5 border border-transparent hover:bg-white/10"
                      }`}
                      data-testid={`health-context-${context.id}`}
                    >
                      <span className={healthContext === context.id ? "text-white" : "text-white/70"}>
                        {context.label}
                      </span>
                    </button>
                  ))}
                </div>
                
                <div className="flex gap-3 mt-4">
                  <motion.button
                    onClick={() => {
                      setHealthContext("skip");
                      setHealthStep(3);
                    }}
                    className="flex-1 py-4 rounded-2xl bg-white/5 border border-white/10 text-white/70 font-medium"
                  >
                    Skip
                  </motion.button>
                  <motion.button
                    onClick={() => setHealthStep(3)}
                    disabled={!healthContext}
                    className={`flex-1 py-4 rounded-2xl font-semibold ${
                      healthContext 
                        ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
                        : "bg-white/10 text-white/30"
                    }`}
                    data-testid="health-continue-2"
                  >
                    Continue
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* Step 1.3: Desired State */}
            {healthStep === 3 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-2xl p-4 mb-4">
                  <p className="text-white/90 text-sm leading-relaxed">
                    "Your body is wise and knows how to heal... What would you like to feel more of right now?"
                  </p>
                </div>
                
                <h3 className="text-white font-medium">What would you like to feel?</h3>
                
                <div className="grid grid-cols-2 gap-2">
                  {HEALTH_DESIRED_STATES.map((state) => (
                    <button
                      key={state.id}
                      onClick={() => setHealthDesiredState(state.id)}
                      className={`p-4 rounded-xl text-left transition-all flex items-center gap-2 ${
                        healthDesiredState === state.id
                          ? "bg-emerald-500/20 border border-emerald-500/40"
                          : "bg-white/5 border border-transparent hover:bg-white/10"
                      }`}
                      data-testid={`health-desired-${state.id}`}
                    >
                      <span className="text-lg">{state.icon}</span>
                      <span className={`text-sm ${healthDesiredState === state.id ? "text-white" : "text-white/70"}`}>
                        {state.label}
                      </span>
                    </button>
                  ))}
                </div>
                
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: healthDesiredState ? 1 : 0.5 }}
                  onClick={async () => {
                    setGeneratingHealthAffirmations(true);
                    try {
                      const response = await axios.post(
                        `${API_URL}/api/private-suite/health/generate-affirmations`,
                        { 
                          feeling: healthFeeling,
                          feeling_text: healthFreeText,
                          context: healthContext,
                          desired_state: healthDesiredState
                        },
                        { headers: { Authorization: `Bearer ${getToken()}` } }
                      );
                      setEditableAffirmations(response.data.affirmations.map(aff => ({
                        text: typeof aff === 'string' ? aff : (aff.text || String(aff)),
                        enabled: true
                      })));
                      setHealthAssistantResponse(response.data.message);
                      toast.success("Your personalized affirmations are ready");
                    } catch (err) {
                      console.error("Error generating health affirmations:", err);
                      toast.error("Using healing defaults");
                      setEditableAffirmations(DEFAULT_HEALTH_AFFIRMATIONS.map(text => ({
                        text, enabled: true
                      })));
                    } finally {
                      setGeneratingHealthAffirmations(false);
                    }
                    setStep(2.5);
                  }}
                  disabled={!healthDesiredState || generatingHealthAffirmations}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold mt-4 flex items-center justify-center gap-2"
                  data-testid="health-generate-btn"
                >
                  {generatingHealthAffirmations ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Creating your affirmations...
                    </>
                  ) : (
                    <>
                      <Sparkles size={18} />
                      Create My Healing Affirmations
                    </>
                  )}
                </motion.button>
                
                <p className="text-center text-white/30 text-xs mt-2">
                  Your input is private and used only to personalize your experience
                </p>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Step 2: Desired State (Meditation) */}
        {step === 2 && mode === "meditation" && (
          <motion.div
            key="desired-state"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="flex-1 flex flex-col p-6"
          >
            <button onClick={() => setStep(1)} className="text-white/60 hover:text-white mb-8">
              <ArrowLeft size={24} />
            </button>
            
            <div className="mb-10">
              <p className="text-white/40 text-sm uppercase tracking-[0.2em] mb-3">Step 2 of 4</p>
              <h2 className="font-heading text-3xl text-white">
                What would you like<br />to feel instead?
              </h2>
              <p className="text-white/30 text-sm mt-4">
                We're not trying to change anything… just allowing you to open
              </p>
            </div>
            
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="grid grid-cols-2 gap-4"
            >
              {DESIRED_STATES.map((state) => {
                const Icon = state.icon;
                const isSelected = desiredState === state.id;
                return (
                  <motion.button
                    key={state.id}
                    variants={itemVariants}
                    onClick={async () => {
                      setDesiredState(state.id);
                      // Load affirmations for this desired state
                      try {
                        const response = await axios.get(
                          `${API_URL}/api/private-suite/meditation/affirmations/${state.id}`,
                          { headers: { Authorization: `Bearer ${getToken()}` } }
                        );
                        setEditableAffirmations(response.data.affirmations.map(aff => ({
                          text: typeof aff === 'string' ? aff : (aff.text || String(aff)),
                          enabled: true
                        })));
                      } catch (err) {
                        console.error("Error loading affirmations:", err);
                        // Use hardcoded defaults
                        const hardcodedDefaults = getDefaultAffirmationsForIntent(state.id);
                        setEditableAffirmations(hardcodedDefaults.map(text => ({
                          text,
                          enabled: true
                        })));
                      }
                      setTimeout(() => setStep(2.5), 300); // Go to affirmation editing
                    }}
                    className={`p-6 rounded-2xl transition-all ${
                      isSelected
                        ? `bg-gradient-to-br ${state.color} text-white scale-105`
                        : "bg-white/5 text-white/70 hover:bg-white/10"
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid={`desired-${state.id}`}
                  >
                    <Icon size={28} className="mb-3" />
                    <p className="font-medium">{state.label}</p>
                  </motion.button>
                );
              })}
            </motion.div>
          </motion.div>
        )}

        {/* Step 2: Focus Area (Performance Mode) */}
        {step === 2 && mode === "performance" && (
          <motion.div
            key="focus-area"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="flex-1 flex flex-col p-6"
          >
            <button onClick={() => setStep(1)} className="text-white/60 hover:text-white mb-8">
              <ArrowLeft size={24} />
            </button>
            
            <div className="mb-8">
              <p className="text-orange-400/80 text-sm uppercase tracking-[0.2em] mb-3">
                {activities.find(a => a.id === selectedActivity)?.label || "Performance"}
              </p>
              <h2 className="font-heading text-3xl text-white">
                What's your focus?
              </h2>
            </div>
            
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="space-y-3"
            >
              {focusAreas.map((focus) => (
                <motion.button
                  key={focus.id}
                  variants={itemVariants}
                  onClick={() => handleFocusAreaSelect(focus.id)}
                  className="w-full p-5 rounded-2xl bg-white/5 border border-white/10 text-left hover:bg-white/10 hover:border-orange-500/30 transition-all group"
                  data-testid={`focus-${focus.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-white">{focus.label}</p>
                      <p className="text-white/50 text-sm mt-1">{focus.description}</p>
                    </div>
                    <ChevronRight className="text-white/30 group-hover:text-orange-400 transition-colors" />
                  </div>
                </motion.button>
              ))}
            </motion.div>
          </motion.div>
        )}

        {/* Step 2.5: Affirmations Editing (All Modes) */}
        {step === 2.5 && (
          <motion.div
            key="affirmations-edit"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="flex-1 flex flex-col p-6 overflow-y-auto"
          >
            <button 
              onClick={() => {
                if (fromAlignmentCheck) {
                  // Go back to home when coming from alignment check
                  navigate('/');
                } else {
                  setStep(mode === "performance" ? 2 : mode === "meditation" ? 2 : 1);
                }
              }} 
              className="text-white/60 hover:text-white mb-6"
            >
              <ArrowLeft size={24} />
            </button>
            
            <div className="mb-6">
              {/* Show context when coming from Alignment Check */}
              {fromAlignmentCheck && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 p-3 rounded-xl bg-[#4A6741]/20 border border-[#4A6741]/30"
                >
                  <p className="text-[#7CB86F] text-sm">
                    Session configured for: <span className="font-medium capitalize">{alignmentIntent}</span>
                  </p>
                </motion.div>
              )}
              
              <p className={`text-sm uppercase tracking-[0.2em] mb-2 ${
                mode === "performance" ? "text-orange-400/80" :
                mode === "deep_recovery" ? "text-slate-400/80" : 
                mode === "health_recovery" ? "text-emerald-400/80" : "text-violet-400/80"
              }`}>Your Inner Dialogue</p>
              <h2 className="font-heading text-2xl text-white">
                {mode === "performance" ? "Program Your Mind" :
                 mode === "deep_recovery" ? "Your Words" : 
                 mode === "health_recovery" ? "Your Healing Words" : "Your Words"}
              </h2>
              <p className="text-white/40 text-sm mt-2">Choose what feels true for you</p>
              
              {/* Selection Counter & Quick Actions */}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
                <span className={`text-sm ${
                  mode === "performance" ? "text-orange-400" :
                  mode === "deep_recovery" ? "text-slate-400" : 
                  mode === "health_recovery" ? "text-emerald-400" : "text-violet-400"
                }`}>
                  {editableAffirmations.filter(a => a.enabled).length} of {editableAffirmations.length} selected
                </span>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setEditableAffirmations(editableAffirmations.map(a => ({ ...a, enabled: true })));
                    }}
                    className="text-xs text-white/50 hover:text-white/80 transition-colors"
                    data-testid="select-all-affirmations"
                  >
                    Select All
                  </button>
                  <span className="text-white/20">|</span>
                  <button
                    onClick={() => {
                      // Keep at least the first one selected
                      setEditableAffirmations(editableAffirmations.map((a, i) => ({ ...a, enabled: i === 0 })));
                    }}
                    className="text-xs text-white/50 hover:text-white/80 transition-colors"
                    data-testid="clear-affirmations"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
            
            <div className="flex-1 space-y-3 mb-6 overflow-y-auto max-h-[50vh]">
              {editableAffirmations.map((aff, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className={`p-4 rounded-xl border ${
                    aff.enabled ? "bg-white/5 border-white/10" : "bg-white/[0.02] border-white/5"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <button 
                      onClick={() => {
                        const updated = [...editableAffirmations];
                        // Don't allow deselecting if it's the last one
                        const enabledCount = updated.filter(a => a.enabled).length;
                        if (updated[idx].enabled && enabledCount <= 1) {
                          return; // Keep at least 1 selected
                        }
                        updated[idx].enabled = !updated[idx].enabled;
                        setEditableAffirmations(updated);
                      }}
                      className={`mt-1 w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                        aff.enabled 
                          ? mode === "performance" ? "bg-orange-500 border-orange-500" :
                            mode === "deep_recovery" ? "bg-slate-500 border-slate-500" : 
                            mode === "health_recovery" ? "bg-emerald-500 border-emerald-500" : "bg-violet-500 border-violet-500"
                          : "border-white/30 hover:border-white/50"
                      }`}
                      data-testid={`affirmation-checkbox-${idx}`}
                    >
                      {aff.enabled && (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M2.5 6L5 8.5L9.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                    <textarea
                      value={aff.text || ''}
                      onChange={(e) => {
                        const updated = [...editableAffirmations];
                        updated[idx].text = e.target.value;
                        setEditableAffirmations(updated);
                      }}
                      placeholder="Enter your affirmation..."
                      className={`flex-1 bg-transparent border-none outline-none resize-none text-sm ${
                        aff.enabled ? "text-white" : "text-white/40"
                      }`}
                      rows={2}
                    />
                    <button 
                      onClick={() => {
                        const updated = editableAffirmations.filter((_, i) => i !== idx);
                        setEditableAffirmations(updated);
                      }}
                      className="text-white/30 hover:text-red-400 transition-colors"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>
                </motion.div>
              ))}
              
              <button
                onClick={() => {
                  setEditableAffirmations([...editableAffirmations, { text: "", enabled: true }]);
                }}
                className="w-full p-4 rounded-xl border border-dashed border-white/20 text-white/50 hover:text-white/80 hover:border-white/40 transition-all flex items-center justify-center gap-2"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                Add Custom Affirmation
              </button>
            </div>
            
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              onClick={handleAffirmationsConfirmed}
              disabled={editableAffirmations.filter(a => a.enabled && a.text && typeof a.text === 'string' && a.text.trim()).length === 0}
              className="rec-chip self-center mt-2"
              data-active={editableAffirmations.filter(a => a.enabled && a.text?.trim()).length > 0}
              data-testid="confirm-affirmations-btn"
              style={{
                padding: "0.95rem 2.2rem",
                fontSize: "0.66rem",
                opacity: editableAffirmations.filter(a => a.enabled && a.text?.trim()).length === 0 ? 0.4 : 1,
                cursor: editableAffirmations.filter(a => a.enabled && a.text?.trim()).length === 0 ? "not-allowed" : "pointer",
              }}
            >
              Continue with {editableAffirmations.filter(a => a.enabled && a.text?.trim()).length} affirmation{editableAffirmations.filter(a => a.enabled && a.text?.trim()).length !== 1 ? 's' : ''}
            </motion.button>
          </motion.div>
        )}

        {/* Step 2.6: Ritual Toggle (Performance Mode Only) */}
        {step === 2.6 && mode === "performance" && (
          <RitualToggle
            sport={selectedActivity}
            sportLabel={activities.find(a => a.id === selectedActivity)?.label}
            ritualEnabled={ritualEnabled}
            setRitualEnabled={setRitualEnabled}
            onBack={() => setStep(2.5)}
            onContinue={handleRitualToggleConfirmed}
          />
        )}

        {/* Step 2.7: Voice Selection (All Modes) */}
        {step === 2.7 && (
          <motion.div
            key="voice-select"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="flex-1 flex flex-col p-6 max-w-3xl mx-auto w-full"
          >
            <EditorialStepHeader
              onBack={() => setStep(2.5)}
              eyebrow="Voice Selection"
              title={mode === "health_recovery" ? "Your voice, your healing" : "Choose your voice"}
              subtitle={
                mode === "health_recovery"
                  ? "Your own voice carries the deepest healing power."
                  : "How should your affirmations sound?"
              }
              backTestid="voice-back-btn"
            />

            <p
              className="italic mb-5"
              style={{
                color: "rgba(215, 187, 122, 0.55)",
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "0.92rem",
                lineHeight: 1.45,
              }}
            >
              {mode === "health_recovery"
                ? "For healing, we recommend recording with your own voice — it resonates most deeply with your subconscious."
                : "We suggest recording with your own voice — it's even more powerful when you record with your own intention."}
            </p>

            <div className="space-y-3 mb-6">
              {/* Health-recovery shows Record FIRST */}
              {mode === "health_recovery" && (
                <EditorialSelectCard
                  icon={<Mic size={20} style={{ color: "#D7BB7A" }} />}
                  title="Record Your Voice"
                  subtitle="Your voice carries the deepest healing power."
                  badge="Recommended"
                  selected={voiceOption === "record"}
                  onClick={() => setVoiceOption("record")}
                  testid="voice-record"
                />
              )}

              {/* Default Reclaim voice (Michael) — hidden for health_recovery */}
              {mode !== "health_recovery" && (
                <EditorialSelectCard
                  icon={<Volume2 size={20} style={{ color: "#D7BB7A" }} />}
                  title="Reclaim Voice"
                  subtitle="Michael's guided voice."
                  selected={voiceOption === "default"}
                  onClick={() => setVoiceOption("default")}
                  testid="voice-default"
                />
              )}

              {/* Record Own Voice — for non-health_recovery */}
              {mode !== "health_recovery" && (
                <EditorialSelectCard
                  icon={<Mic size={20} style={{ color: "#D7BB7A" }} />}
                  title="Record Own Voice"
                  subtitle="This creates the deepest change."
                  selected={voiceOption === "record"}
                  onClick={() => setVoiceOption("record")}
                  testid="voice-record"
                />
              )}

              {/* User's AI Clone Voice — health_recovery secondary slot */}
              {mode === "health_recovery" && aiVoiceUsage?.has_access && aiVoiceUsage?.has_voice_clone && (
                <div>
                  <EditorialSelectCard
                    icon={<Sparkles size={20} style={{ color: "#D7BB7A" }} />}
                    title="Your AI Voice"
                    subtitle={`Use your cloned voice (${aiVoiceUsage?.voice_clone?.name || "My Voice"})`}
                    badge={`${aiVoiceUsage?.remaining || 0}/${aiVoiceUsage?.monthly_limit || 20}`}
                    selected={voiceOption === "clone"}
                    disabled={!aiVoiceUsage?.can_generate}
                    onClick={() => {
                      if (aiVoiceUsage?.can_generate) setVoiceOption("clone");
                      else toast.error(aiVoiceUsage?.message || "AI voice limit reached");
                    }}
                    testid="voice-clone"
                  />
                  <button
                    onClick={(e) => { e.stopPropagation(); playAiVoiceDemo(); }}
                    className="mt-2 flex items-center gap-1.5 text-xs ml-auto"
                    style={{ color: "rgba(215, 187, 122, 0.75)", fontFamily: '"Manrope", sans-serif' }}
                    data-testid="preview-ai-voice-btn"
                  >
                    {playingAiDemo ? <Pause size={12} /> : <Play size={12} />}
                    {playingAiDemo ? "Stop Preview" : "Preview my voice"}
                  </button>
                </div>
              )}

              {/* AI Clone Voice — non-health_recovery */}
              {mode !== "health_recovery" && (
                <>
                  {aiVoiceLoading ? (
                    <div className="w-full p-4 rounded-2xl bg-white/5 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-amber-300 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : aiVoiceUsage?.has_access ? (
                    <div>
                      <EditorialSelectCard
                        icon={<Sparkles size={20} style={{ color: "#D7BB7A" }} />}
                        title="AI Clone Voice"
                        subtitle={
                          aiVoiceUsage?.has_voice_clone
                            ? `Use your cloned voice (${aiVoiceUsage?.voice_clone?.name || "My Voice"})`
                            : "Set up your voice clone in Voice Studio first."
                        }
                        badge={`${aiVoiceUsage?.remaining || 0}/${aiVoiceUsage?.monthly_limit || 20}`}
                        selected={voiceOption === "clone"}
                        disabled={!aiVoiceUsage?.can_generate}
                        onClick={() => {
                          if (aiVoiceUsage?.can_generate) setVoiceOption("clone");
                          else toast.error(aiVoiceUsage?.message || "AI voice limit reached");
                        }}
                        testid="voice-clone"
                      />
                      {!aiVoiceUsage?.can_generate && (
                        <p
                          className="mt-2 text-xs italic"
                          style={{ color: "rgba(215, 187, 122, 0.55)", fontFamily: '"Manrope", sans-serif' }}
                        >
                          Monthly limit reached. Contact us or use Record Own Voice.
                        </p>
                      )}
                      {aiVoiceUsage?.has_voice_clone && (
                        <button
                          onClick={(e) => { e.stopPropagation(); playAiVoiceDemo(); }}
                          className="mt-2 flex items-center gap-1.5 text-xs ml-auto"
                          style={{ color: "rgba(215, 187, 122, 0.75)", fontFamily: '"Manrope", sans-serif' }}
                          data-testid="preview-ai-voice-btn"
                        >
                          {playingAiDemo ? <Pause size={12} /> : <Play size={12} />}
                          {playingAiDemo ? "Stop Preview" : "Preview my voice"}
                        </button>
                      )}
                    </div>
                  ) : (
                    /* Locked state for non-premium */
                    <div
                      className="w-full p-5 rounded-2xl"
                      style={{
                        background: "linear-gradient(155deg, rgba(13, 36, 56, 0.5) 0%, rgba(10, 22, 38, 0.7) 100%)",
                        border: "1px dashed rgba(215, 187, 122, 0.18)",
                      }}
                    >
                      <div className="flex items-center gap-4 opacity-65">
                        <div
                          className="w-11 h-11 rounded-xl flex items-center justify-center"
                          style={{ background: "rgba(215, 187, 122, 0.06)", border: "1px solid rgba(215, 187, 122, 0.15)" }}
                        >
                          <Lock size={18} style={{ color: "#D7BB7A" }} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p
                              style={{
                                fontFamily: '"Cormorant Garamond", serif',
                                color: "rgba(243, 237, 221, 0.7)",
                                fontSize: "1.05rem",
                              }}
                            >
                              AI Clone Voice
                            </p>
                            <span
                              className="text-[10px] px-2 py-0.5 rounded-full uppercase"
                              style={{
                                fontFamily: '"Manrope", sans-serif',
                                letterSpacing: "0.18em",
                                background: "rgba(215, 187, 122, 0.18)",
                                color: "#D7BB7A",
                                border: "1px solid rgba(215, 187, 122, 0.32)",
                              }}
                            >
                              Premium
                            </span>
                          </div>
                          <p
                            className="italic mt-1"
                            style={{
                              color: "rgba(243, 237, 221, 0.45)",
                              fontFamily: '"Cormorant Garamond", serif',
                              fontSize: "0.85rem",
                            }}
                          >
                            Upgrade to Premium to use AI voice cloning.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); playAiVoiceDemo(); }}
                        className="mt-3 w-full rec-chip justify-center"
                        style={{ padding: "0.6rem 1rem", fontSize: "0.58rem" }}
                        data-testid="listen-demo-btn"
                      >
                        {playingAiDemo ? <Pause size={12} /> : <Play size={12} />}
                        {playingAiDemo ? "Stop Demo" : "Listen to Demo"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            <motion.button
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              onClick={handleVoiceConfirmed}
              disabled={generatingAiVoice || (voiceOption === "clone" && !aiVoiceUsage?.has_voice_clone)}
              className="rec-chip self-center mt-2"
              data-active={!(generatingAiVoice || (voiceOption === "clone" && !aiVoiceUsage?.has_voice_clone))}
              data-testid="confirm-voice-btn"
              style={{
                padding: "0.95rem 2.2rem",
                fontSize: "0.66rem",
                opacity:
                  generatingAiVoice || (voiceOption === "clone" && !aiVoiceUsage?.has_voice_clone) ? 0.4 : 1,
                cursor:
                  generatingAiVoice || (voiceOption === "clone" && !aiVoiceUsage?.has_voice_clone)
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {generatingAiVoice ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Generating AI Voice…
                </>
              ) : voiceOption === "clone" && !aiVoiceUsage?.has_voice_clone ? (
                "Set up voice clone in Voice Studio first"
              ) : voiceOption === "record" ? (
                "Continue to Record →"
              ) : voiceOption === "clone" ? (
                "Generate with AI Voice →"
              ) : (
                "Continue to Music →"
              )}
            </motion.button>
          </motion.div>
        )}

        {/* Step 2.8: Recording Screen (when "Record Own Voice" is selected) */}
        {step === 2.8 && (
          <motion.div
            key="recording"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="flex-1 flex flex-col p-6"
          >
            <button onClick={() => setStep(2.7)} className="text-white/60 hover:text-white mb-6">
              <ArrowLeft size={24} />
            </button>
            
            <div className="text-center mb-6">
              <Mic className={`w-10 h-10 mx-auto mb-3 ${
                mode === "performance" ? "text-orange-400" :
                mode === "deep_recovery" ? "text-slate-400" : "text-violet-400"
              }`} />
              <h2 className="text-xl font-heading text-white mb-2">Record Your Affirmations</h2>
              <p className="text-white/60 text-sm max-w-sm mx-auto">
                Your voice has powerful influence over your subconscious mind. Read each affirmation aloud while recording.
              </p>
            </div>

            {/* Affirmations List - Read While Recording */}
            <div className={`w-full mb-6 rounded-xl p-4 max-h-48 overflow-y-auto ${
              mode === "performance" ? "bg-orange-500/10" :
              mode === "deep_recovery" ? "bg-slate-500/10" : "bg-violet-500/10"
            }`}>
              <p className={`text-xs uppercase tracking-wider mb-3 text-center ${
                mode === "performance" ? "text-orange-400/60" :
                mode === "deep_recovery" ? "text-slate-400/60" : "text-violet-400/60"
              }`}>
                Read these affirmations aloud
              </p>
              <div className="space-y-2">
                {editableAffirmations.filter(a => a.enabled && a.text?.trim()).map((aff, index) => (
                  <motion.div
                    key={index}
                    animate={{
                      backgroundColor: isRecording && index === currentReadingIndex 
                        ? mode === "performance" ? "rgba(249, 115, 22, 0.2)" :
                          mode === "deep_recovery" ? "rgba(100, 116, 139, 0.2)" : "rgba(139, 92, 246, 0.2)"
                        : "transparent",
                      scale: isRecording && index === currentReadingIndex ? 1.02 : 1
                    }}
                    className={`p-2 rounded-lg transition-all ${
                      isRecording && index === currentReadingIndex
                        ? mode === "performance" ? "border border-orange-500/50" :
                          mode === "deep_recovery" ? "border border-slate-500/50" : "border border-violet-500/50"
                        : index < currentReadingIndex && isRecording
                          ? "opacity-40"
                          : ""
                    }`}
                  >
                    <p className={`text-sm ${
                      isRecording && index === currentReadingIndex
                        ? "text-white font-medium"
                        : "text-white/70"
                    }`}>
                      {index + 1}. "{aff.text}"
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Record Button */}
            <div className="flex flex-col items-center">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={isRecording ? stopRecording : startRecording}
                className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 shadow-lg ${
                  isRecording 
                    ? "bg-red-500 animate-pulse" 
                    : mode === "performance" ? "bg-gradient-to-br from-orange-500 to-red-500" :
                      mode === "deep_recovery" ? "bg-gradient-to-br from-slate-500 to-indigo-500" :
                      "bg-gradient-to-br from-violet-500 to-purple-600"
                }`}
                data-testid="record-btn"
              >
                {isRecording ? (
                  <Square className="w-7 h-7 text-white" />
                ) : (
                  <Mic className="w-9 h-9 text-white" />
                )}
              </motion.button>

              {isRecording ? (
                <p className="text-red-400 text-sm mb-4">Recording... Tap to stop</p>
              ) : (
                <p className="text-white/50 text-xs mb-4">Tap to start recording</p>
              )}

              {recordingError && !isRecording && (
                <div className="w-full max-w-sm mb-4 p-3 bg-red-500/20 rounded-lg text-red-300 text-sm text-center">
                  {recordingError}
                </div>
              )}

              {/* Playback Preview */}
              {recordingUrl && !isRecording && (
                <div className={`w-full max-w-sm rounded-xl p-4 mb-4 ${
                  mode === "performance" ? "bg-orange-500/10" :
                  mode === "deep_recovery" ? "bg-slate-500/10" : "bg-violet-500/10"
                }`}>
                  <audio 
                    ref={recordingPlaybackRef} 
                    src={recordingUrl}
                    onEnded={() => setIsPlayingRecording(false)}
                  />
                  <div className="flex items-center gap-4">
                    <button
                      onClick={toggleRecordingPlayback}
                      className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        mode === "performance" ? "bg-orange-500/20" :
                        mode === "deep_recovery" ? "bg-slate-500/20" : "bg-violet-500/20"
                      }`}
                      data-testid="playback-toggle-btn"
                    >
                      {isPlayingRecording ? (
                        <Pause className={`w-5 h-5 ${
                          mode === "performance" ? "text-orange-400" :
                          mode === "deep_recovery" ? "text-slate-400" : "text-violet-400"
                        }`} />
                      ) : (
                        <Play className={`w-5 h-5 ml-0.5 ${
                          mode === "performance" ? "text-orange-400" :
                          mode === "deep_recovery" ? "text-slate-400" : "text-violet-400"
                        }`} />
                      )}
                    </button>
                    <div className="flex-1">
                      <p className="text-white text-sm">Your Recording</p>
                      <p className="text-white/50 text-xs">Tap to preview</p>
                    </div>
                    <button
                      onClick={clearRecording}
                      className="text-white/40 hover:text-white/70 text-xs"
                      data-testid="re-record-btn"
                    >
                      Re-record
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Continue Button */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              onClick={() => setStep(3)}
              disabled={!recordingUrl}
              className="rec-chip self-center mt-auto"
              data-active={!!recordingUrl}
              data-testid="continue-after-recording-btn"
              style={{
                padding: "0.95rem 2.2rem",
                fontSize: "0.66rem",
                opacity: !recordingUrl ? 0.4 : 1,
                cursor: !recordingUrl ? "not-allowed" : "pointer",
              }}
            >
              {mode === "performance" ? "Continue to Music" : "Continue to Duration"}
            </motion.button>
          </motion.div>
        )}

        {/* Step 3: Duration */}
        {step === 3 && (
          <motion.div
            key="duration"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="flex-1 flex flex-col p-6 overflow-y-auto max-w-3xl mx-auto w-full"
          >
            <EditorialStepHeader
              onBack={() => setStep(voiceOption === "record" ? 2.8 : 2.7)}
              eyebrow={
                mode === "performance" ? "Performance Mode" :
                mode === "deep_recovery" ? "Deep Recovery" :
                mode === "health_recovery" ? "Health Recovery" :
                "Calm · Meditation"
              }
              title="Configure your session"
              subtitle="Choose your music, layer binaural beats, set the whisper, and pick your length."
              backTestid="duration-back-btn"
            />

            {/* Signature Mode UI is intentionally MINIMAL — user
                feedback Feb 27 2026: "got the signature mode, the
                only selection they should have is the time, let's
                have it so simple — 3, 6 or 9 and continuous". The
                music, voice, atmosphere, binaural are all
                pre-curated for the chosen signature, so we hide the
                ConfigureSession panel and only show the time picker. */}
            {!signatureMode && (
              <ConfigureSession
                premiumMusicTrack={premiumMusicTrack}
                onOpenMusicPicker={() => setShowMusicPicker(true)}
                binauralEnabled={binauralEnabled}
                setBinauralEnabled={setBinauralEnabled}
                binauralBeat={binauralBeat}
                onOpenBinauralPicker={() => setShowBinauralPicker(true)}
              />
            )}

            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="space-y-3"
            >
              {(() => {
                // Signature mode uses a tighter 3/6/9/continuous set
                // — the curated music + affirmation count are tuned
                // to those loop multiples.
                const SIGNATURE_DURATIONS = [
                  { id: "3min", label: "3 minutes", seconds: 180, description: "One full loop · arrival install" },
                  { id: "6min", label: "6 minutes", seconds: 360, description: "Two loops · deep install" },
                  { id: "9min", label: "9 minutes", seconds: 540, description: "Three loops · embodiment" },
                  { id: "continuous", label: "Continuous", seconds: null, description: "Loop until you stop" },
                ];
                const list = signatureMode ? SIGNATURE_DURATIONS : DURATIONS;
                return list;
              })().map((d) => {
                const isSelected = duration === d.id;
                const IconComponent = d.id === 'continuous' ? Infinity : Clock;
                // Free users from alignment check can only select 5min
                const isPreviewUser = fromAlignmentCheck && !hasAccess;
                const isLocked = isPreviewUser && d.id !== '5min';
                const isCustom = !!d.custom;

                return (
                  <motion.button
                    key={d.id}
                    variants={itemVariants}
                    onClick={() => !isLocked && setDuration(d.id)}
                    disabled={isLocked}
                    className="w-full p-5 rounded-2xl text-left transition-all flex items-center justify-between"
                    style={{
                      background: isSelected
                        ? "linear-gradient(155deg, rgba(215, 187, 122, 0.18) 0%, rgba(13, 36, 56, 0.92) 100%)"
                        : "linear-gradient(155deg, rgba(13, 36, 56, 0.78) 0%, rgba(10, 22, 38, 0.92) 100%)",
                      border: isSelected
                        ? "1px solid rgba(215, 187, 122, 0.45)"
                        : "1px solid rgba(215, 187, 122, 0.10)",
                      boxShadow: isSelected ? "0 0 18px rgba(215, 187, 122, 0.15)" : "none",
                      opacity: isLocked ? 0.4 : 1,
                      cursor: isLocked ? "not-allowed" : "pointer",
                    }}
                    data-testid={`duration-${d.id}`}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <IconComponent size={20} style={{ color: "#D7BB7A", opacity: isSelected ? 1 : 0.7 }} />
                      <div className="flex-1">
                        <p
                          style={{
                            fontFamily: '"Cormorant Garamond", Georgia, serif',
                            color: "#F3EDDD",
                            fontSize: "1.15rem",
                            lineHeight: 1.3,
                          }}
                        >
                          {d.label}
                          {isLocked && (
                            <span
                              className="ml-2 text-[10px] uppercase inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                              style={{
                                fontFamily: '"Manrope", sans-serif',
                                letterSpacing: "0.18em",
                                background: "rgba(215, 187, 122, 0.18)",
                                color: "#D7BB7A",
                                border: "1px solid rgba(215, 187, 122, 0.32)",
                              }}
                            >
                              <Lock size={10} />
                              Premium
                            </span>
                          )}
                        </p>
                        {d.description && (
                          <p
                            className="italic mt-0.5"
                            style={{
                              color: "rgba(243, 237, 221, 0.48)",
                              fontFamily: '"Cormorant Garamond", serif',
                              fontSize: "0.88rem",
                            }}
                          >
                            {d.description}
                          </p>
                        )}
                        {isCustom && isSelected && (
                          <div className="flex items-center gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="number"
                              inputMode="numeric"
                              min={1}
                              max={60}
                              value={customDurationMinutes}
                              onChange={(e) => {
                                const v = Math.max(1, Math.min(60, parseInt(e.target.value || "1", 10) || 1));
                                setCustomDurationMinutes(v);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-20 px-3 py-2 rounded-lg text-center"
                              style={{
                                background: "rgba(215, 187, 122, 0.10)",
                                border: "1px solid rgba(215, 187, 122, 0.32)",
                                color: "#F3EDDD",
                                fontFamily: '"Manrope", sans-serif',
                              }}
                              data-testid="duration-custom-input"
                            />
                            <span
                              className="italic"
                              style={{
                                color: "rgba(243, 237, 221, 0.55)",
                                fontFamily: '"Cormorant Garamond", serif',
                                fontSize: "0.9rem",
                              }}
                            >
                              minutes (1–60)
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {isSelected && !isLocked && (
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: "linear-gradient(135deg, #F5E0AD 0%, #A88A4C 100%)" }}
                      >
                        <Check size={14} style={{ color: "#0a1a2e", strokeWidth: 3 }} />
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </motion.div>
            
            {/* Subliminal Mode toggle — two precise volume presets:
                  ON  → server-side -28dB whisper, barely audible
                  OFF → full voice, clearly audible under the music
                The toggle avoids the iOS Safari `volume` API quirk where
                slider positions were inaudible — server pre-bakes the
                quiet version of the file so it Just Works. */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mt-6 p-4 bg-white/5 rounded-xl"
              data-testid="subliminal-toggle-card"
            >
              <button
                onClick={() => {
                  const newValue = !subliminalMode;
                  setSubliminalMode(newValue);
                  subliminalModeRef.current = newValue;
                }}
                className="w-full flex items-center justify-between"
                data-testid="subliminal-toggle-btn"
              >
                <div className="text-left">
                  <p className="text-white/80 font-medium text-sm">Subliminal Mode</p>
                  <p className="text-white/40 text-xs mt-1">
                    {subliminalMode
                      ? "Whisper level · barely audible beneath the music"
                      : "Voice forward · clearly audible affirmations"}
                  </p>
                </div>
                <div className={`w-12 h-7 rounded-full transition-colors ${
                  subliminalMode ? "bg-violet-500" : "bg-white/20"
                }`}>
                  <div className={`w-5 h-5 bg-white rounded-full mt-1 transition-transform ${
                    subliminalMode ? "translate-x-6" : "translate-x-1"
                  }`} />
                </div>
              </button>
            </motion.div>
            
            {/* "Advanced Settings" panel hidden Feb 27 2026 — user
                feedback: "with the custom, we do not need the advanced
                settings option". Atmosphere + Brain State now live
                under the per-mode ConfigureSession picker, so showing
                them again here was redundant. Kept as `false &&` so we
                can flip it back on with one-line change if needed. */}
            {false && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-4"
            >
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full flex items-center justify-between text-white/50 hover:text-white/70 transition-colors py-2"
              >
                <div className="flex items-center gap-2">
                  <Settings2 size={16} />
                  <span className="text-sm">Advanced Settings</span>
                </div>
                <motion.div
                  animate={{ rotate: showAdvanced ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown size={18} />
                </motion.div>
              </button>
              
              <AnimatePresence>
                {showAdvanced && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-4 space-y-6">
                      {/* Atmosphere Section */}
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Music size={16} className="text-violet-400" />
                          <p className="text-white/70 text-sm font-medium">Atmosphere</p>
                        </div>
                        <p className="text-white/30 text-xs mb-3">Background sound environment</p>
                        <div className="flex gap-2 flex-wrap">
                          {[
                            { id: "ambient", label: "Ambient Music", icon: Music },
                            { id: "nature", label: "Nature Sounds", icon: Waves },
                            { id: "silence", label: "Silence", icon: VolumeX }
                          ].map((option) => {
                            const Icon = option.icon;
                            return (
                              <button
                                key={option.id}
                                onClick={() => setAtmosphere(option.id)}
                                className={`px-4 py-2.5 rounded-xl text-sm transition-all flex items-center gap-2 ${
                                  atmosphere === option.id
                                    ? "bg-violet-500/30 text-white border border-violet-500/50"
                                    : "bg-white/5 text-white/60 border border-transparent hover:bg-white/10"
                                }`}
                                data-testid={`atmosphere-${option.id}`}
                              >
                                <Icon size={14} />
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Brain State Section */}
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Brain size={16} className="text-violet-400" />
                          <p className="text-white/70 text-sm font-medium">Brain State</p>
                        </div>
                        <p className="text-white/30 text-xs mb-3">Binaural beats (headphones recommended)</p>
                        <div className="flex gap-2 flex-wrap">
                          {[
                            { id: "none", label: "None", desc: "" },
                            { id: "alpha", label: "Alpha", desc: "Calm / Relaxed Focus" },
                            { id: "theta", label: "Theta", desc: "Deep Meditation" },
                            { id: "delta", label: "Delta", desc: "Sleep / Deep Rest" },
                            { id: "beta", label: "Beta", desc: "Focus / Clarity" }
                          ].map((beat) => (
                            <button
                              key={beat.id}
                              onClick={() => setBrainState(beat.id)}
                              className={`px-4 py-2.5 rounded-xl text-sm transition-all ${
                                brainState === beat.id
                                  ? "bg-violet-500/30 text-white border border-violet-500/50"
                                  : "bg-white/5 text-white/60 border border-transparent hover:bg-white/10"
                              }`}
                              data-testid={`brain-state-${beat.id}`}
                            >
                              <span>{beat.label}</span>
                              {beat.desc && (
                                <span className="text-white/30 text-xs ml-1">· {beat.desc}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
            )}

            <div className="mt-auto pt-8 space-y-3 relative">
              {/* D3.54 — Begin Session · gilded dot + chip, signature
                  expansion portal on tap to align with Team Alignment's
                  Enter-Immersion language. */}
              <div className="flex flex-col items-center gap-4">
                {duration && !loading && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      boxShadow: [
                        "0 0 22px rgba(215,187,122,0.5)",
                        "0 0 38px rgba(215,187,122,0.85)",
                        "0 0 22px rgba(215,187,122,0.5)",
                      ],
                    }}
                    transition={{
                      opacity: { duration: 0.5 },
                      scale: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
                      boxShadow: { duration: 2.8, repeat: Infinity, ease: "easeInOut" },
                    }}
                    onClick={() => {
                      if (beginExpanding) return;
                      setBeginExpanding(true);
                      setTimeout(() => generateSession(), 1100);
                    }}
                    disabled={beginExpanding}
                    className="rounded-full"
                    style={{
                      width: "16px",
                      height: "16px",
                      background: "radial-gradient(circle at 30% 30%, #F5E0AD 0%, #D7BB7A 55%, #A88A4C 100%)",
                      border: "1px solid rgba(245,224,173,0.55)",
                      cursor: "pointer",
                    }}
                    aria-label="Begin Session"
                    data-testid="begin-session-dot"
                  />
                )}
                <button
                  onClick={() => {
                    if (beginExpanding || !duration || loading) return;
                    setBeginExpanding(true);
                    setTimeout(() => generateSession(), 1100);
                  }}
                  disabled={!duration || loading || beginExpanding}
                  className="rec-chip"
                  data-active={!!duration && !loading}
                  data-testid="generate-session-btn"
                  style={{
                    padding: "0.95rem 2.2rem",
                    fontSize: "0.7rem",
                    opacity: !duration || loading ? 0.4 : 1,
                    cursor: !duration || loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "Creating your session…" : "Begin Session"}
                </button>
              </div>
              {/* Surface the "you can save this" promise BEFORE the
                  session starts (the actual Save action requires a
                  session_id and lives on the reflection screen).
                  Removes the confusion: "where do I save / download?" */}
              <p
                className="text-center text-white/45 text-xs flex items-center justify-center gap-2"
                data-testid="library-promise-hint"
              >
                <Bookmark size={11} />
                After this session you can save it to your library
                {isPremiumUser ? " + download MP3" : " · download with Premium"}
              </p>

              {/* Signature dot expansion portal — fills the screen as the
                  threshold into the session player. Mirrors the Team
                  Alignment "Enter Immersion" portal so the brand has one
                  recognisable cue for crossing into an immersion. */}
              <AnimatePresence>
                {beginExpanding && (
                  <motion.div
                    key="ps-begin-portal"
                    initial={{ scale: 0, opacity: 1 }}
                    animate={{ scale: 220, opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    className="fixed rounded-full pointer-events-none"
                    style={{
                      width: "16px",
                      height: "16px",
                      left: "50%",
                      bottom: "8rem",
                      transform: "translate(-50%, 50%)",
                      background:
                        "radial-gradient(circle at 30% 30%, #F5E0AD 0%, #D7BB7A 50%, #A88A4C 100%)",
                      boxShadow:
                        "0 0 80px rgba(215,187,122,0.9), 0 0 200px rgba(215,187,122,0.6)",
                      zIndex: 100,
                    }}
                    data-testid="ps-begin-dot-portal"
                  />
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}

        {/* Step 3.5: Pre-Performance Ritual Player */}
        {step === 3.5 && mode === "performance" && ritualData && (
          <PrePerformanceRitual
            sport={selectedActivity}
            ritual={ritualData}
            onComplete={handleRitualComplete}
            onSkip={handleRitualSkip}
            authToken={getToken()}
          />
        )}

        {/* Step 4: Session Player */}
        {step === 4 && (
          <motion.div
            key="session"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="flex-1 flex flex-col bg-gradient-to-b from-[#0f0f1a] to-[#1a1a2e]"
          >
            {sessionPhase === "reflection" ? (
              /* Post-Session Reflection Screen */
              <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6 }}
                  className="w-20 h-20 bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-full flex items-center justify-center mb-8"
                >
                  <Heart size={32} className="text-violet-300" />
                </motion.div>
                
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="font-heading text-2xl text-white mb-3"
                >
                  How do you feel now?
                </motion.h2>
                
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-white/40 text-sm mb-10 max-w-xs"
                >
                  Take a moment to notice what has shifted
                </motion.p>
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="flex flex-wrap justify-center gap-3 max-w-sm"
                >
                  {[
                    { id: "calm", label: "Calm", icon: Heart, color: "from-emerald-400/20 to-teal-400/20" },
                    { id: "clear", label: "Clear", icon: Eye, color: "from-cyan-400/20 to-blue-400/20" },
                    { id: "energised", label: "Energised", icon: Zap, color: "from-amber-400/20 to-orange-400/20" },
                    { id: "present", label: "Present", icon: Target, color: "from-violet-400/20 to-purple-400/20" },
                    { id: "still_unsettled", label: "Still Unsettled", icon: Moon, color: "from-slate-400/20 to-gray-400/20" },
                  ].map((feeling, idx) => {
                    const Icon = feeling.icon;
                    const isSelected = postSessionFeeling === feeling.id;
                    return (
                      <motion.button
                        key={feeling.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5 + idx * 0.05 }}
                        onClick={() => submitReflection(feeling.id)}
                        disabled={postSessionFeeling !== null}
                        className={`px-5 py-3 rounded-2xl flex items-center gap-2 transition-all ${
                          isSelected
                            ? `bg-gradient-to-br ${feeling.color} border border-white/30 scale-105`
                            : "bg-white/5 border border-transparent hover:bg-white/10"
                        } ${postSessionFeeling !== null && !isSelected ? "opacity-40" : ""}`}
                        data-testid={`reflection-${feeling.id}`}
                      >
                        <Icon size={18} className={isSelected ? "text-white" : "text-white/60"} />
                        <span className={`text-sm font-medium ${isSelected ? "text-white" : "text-white/70"}`}>
                          {feeling.label}
                        </span>
                      </motion.button>
                    );
                  })}
                </motion.div>
                
                {postSessionFeeling && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mt-10 flex flex-col items-center gap-3 max-w-sm w-full px-4"
                  >
                    <p className="text-white/30 text-xs mb-1" data-testid="reflection-thanks">
                      Thank you for sharing
                    </p>
                    <SaveSessionToLibrary sessionId={sessionData?.session_id} isPremium={isPremiumUser} />
                  </motion.div>
                )}
              </div>
            ) : sessionPhase !== "complete" ? (
              <>
                {/* Animated Background - Using CSS animations to avoid WAAPI issues */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  {/* Gradient orbs that slowly move */}
                  <div 
                    className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-600/10 rounded-full blur-3xl animate-slow-drift-1"
                  />
                  <div 
                    className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-600/10 rounded-full blur-3xl animate-slow-drift-2"
                  />
                  <div 
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-indigo-500/5 rounded-full blur-2xl animate-slow-drift-3"
                  />
                </div>

                {/* Session Header */}
                <div className="relative z-10 p-6 flex items-center justify-between">
                  <button
                    onClick={() => {
                      pauseSession();
                      setStep(3);
                    }}
                    className="text-white/40 hover:text-white"
                  >
                    <ArrowLeft size={24} />
                  </button>
                  <div className="text-center">
                    <p className="text-white/40 text-xs uppercase tracking-[0.15em]">
                      {sessionPhase === "intro" && "Preparing"}
                      {sessionPhase === "breathing" && `Breath ${breathCount + 1} of ${sessionData?.breathing_config?.cycles || 3}`}
                      {sessionPhase === "affirmations" && "In Session"}
                    </p>
                    <p className="text-white/60 text-sm mt-1">
                      {formatTime(elapsedTime)} / {formatTime(sessionData?.duration_seconds)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Subliminal Mode Badge */}
                    {subliminalMode && (
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-violet-500/20 rounded-full border border-violet-500/30">
                        <VolumeX size={12} className="text-violet-400" />
                        <span className="text-violet-300 text-[10px] font-medium">SUB</span>
                      </div>
                    )}
                    {/* Layered Binaural Badge — quietly tells the user
                        which brainwave they're entrained to. Screenshot-friendly
                        premium differentiator. */}
                    {binauralEnabled && binauralBeat && (
                      <div
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                        style={{
                          background: "rgba(241,210,156,0.10)",
                          border: "1px solid rgba(241,210,156,0.30)",
                        }}
                        data-testid="layered-badge"
                        title={`Layered: ${binauralBeat.label}`}
                      >
                        <Waves size={11} className="text-amber-200" />
                        <span className="text-amber-200 text-[10px] tracking-[0.12em] uppercase font-medium">
                          Layered · {binauralBeat.label}
                        </span>
                      </div>
                    )}
                    <button onClick={toggleMute} className="text-white/40 hover:text-white">
                      {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
                    </button>
                  </div>
                </div>
                
                {/* Main Session Area */}
                <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-8">
                  {/* Fixed-height orb wrapper — locks the visual baseline so
                      transitioning between intro/breathing/affirmation phases
                      never shifts surrounding content up or down. */}
                  <div
                    className="flex items-center justify-center"
                    style={{ width: 240, height: 240, flexShrink: 0 }}
                  >
                    <PremiumOrb
                      size={220}
                      hue="gold"
                      mode={
                        binauralEnabled && binauralBeat
                          ? 'binaural'
                          : (sessionPhase === 'intro' ? 'static' : 'breath')
                      }
                      hz={binauralBeat ? (BINAURAL_HZ[binauralBeat.id?.split('_')[0]] || 6) : 6}
                      testId={`session-orb-${sessionPhase}`}
                    />
                  </div>

                  {/* Fixed-height phase-text slot — same vertical footprint
                      whether breathing/intro/affirmation text is shown. Prevents
                      the orb above and the player controls below from sliding.
                      Slightly more generous on mobile to fit a 3-line affirmation. */}
                  <div
                    className="mt-10 px-6 text-center flex items-center justify-center"
                    style={{ minHeight: 'clamp(112px, 14vh, 132px)', width: '100%' }}
                  >
                    <AnimatePresence mode="wait">
                      {sessionPhase === 'breathing' && (
                        <motion.p
                          key={`breath-${breathPhase}`}
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                          transition={{ duration: 0.5 }}
                          className="text-amber-100/60 text-sm tracking-[0.3em] uppercase"
                        >
                          {breathPhase === 'inhale' ? 'Breathe In' :
                           breathPhase === 'hold' ? 'Hold' :
                           breathPhase === 'release' ? 'Release' : 'Rest'}
                        </motion.p>
                      )}

                      {sessionPhase === 'affirmations' && (
                        // Affirmations are synced 1:1 — the audio is
                        // ElevenLabs-synthesised from the exact `aff.text`
                        // that gets displayed (see backend
                        // `generate-session` line 1079: it iterates the
                        // same affirmation_texts array for both audio
                        // generation AND the dictionary entry that
                        // becomes `aff.text` here). So whatever shows
                        // matches what's whispered.
                        //
                        // In subliminal mode we keep the words visible
                        // but render them very faintly in our premium
                        // Cormorant italic — the brain anchors to the
                        // exact words, conscious mind stays soft. User
                        // calibration Feb 27 2026: "show the exact
                        // words playing subtly on the screen — but they
                        // have to be the exact words and show them in
                        // a faint premium font."
                        currentAffirmationText ? (
                          <motion.p
                            key={currentAffirmationText}
                            initial={{ opacity: 0, letterSpacing: '0.1em' }}
                            animate={{
                              opacity: subliminalMode ? 0.45 : 0.92,
                              letterSpacing: '0.04em',
                            }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 1.4, ease: 'easeOut' }}
                            className="leading-relaxed max-w-md mx-auto px-4 italic text-center"
                            style={{
                              fontFamily: '"Cormorant Garamond", Georgia, serif',
                              fontSize: subliminalMode ? '1.25rem' : '1.35rem',
                              fontWeight: 300,
                              color: subliminalMode ? '#F3EDDD' : 'rgba(255,255,255,0.92)',
                              textShadow: subliminalMode
                                ? '0 0 28px rgba(215, 187, 122, 0.18)'
                                : '0 0 16px rgba(255,255,255,0.12)',
                            }}
                            data-testid="aff-current-text"
                          >
                            {currentAffirmationText}
                          </motion.p>
                        ) : (
                          <motion.p
                            key="affirm-listen"
                            initial={{ opacity: 0 }} animate={{ opacity: 0.5 }} exit={{ opacity: 0 }}
                            className="text-amber-100/45 text-xs tracking-[0.32em] uppercase"
                          >
                            {subliminalMode ? 'Installing' : 'Listen'}
                          </motion.p>
                        )
                      )}

                      {sessionPhase === 'intro' && (
                        <motion.p
                          key="intro-arrive"
                          initial={{ opacity: 0 }} animate={{ opacity: 0.6 }} exit={{ opacity: 0 }}
                          transition={{ duration: 0.6 }}
                          className="text-amber-100/60 text-sm tracking-[0.3em] uppercase"
                        >
                          Let yourself arrive
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                      
                      {/* Subliminal Mode Indicator */}
                      {subliminalMode && sessionPhase === "affirmations" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-6 flex items-center justify-center gap-2 px-4 py-2 bg-violet-500/20 rounded-full border border-violet-500/30"
                        >
                          <VolumeX size={14} className="text-violet-400" />
                          <span className="text-violet-300 text-xs font-medium tracking-wide">SUBLIMINAL MODE</span>
                        </motion.div>
                      )}
                </div>
                
                {/* Play/Pause Control */}
                <div className="relative z-10 p-8 flex justify-center">
                  {!isPlaying ? (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={startSession}
                      className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-[0_0_30px_rgba(255,255,255,0.2)]"
                      data-testid="play-session-btn"
                    >
                      <Play size={32} className="text-[#1a1a2e] ml-1" />
                    </motion.button>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={pauseSession}
                      className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm"
                    >
                      <Pause size={32} className="text-white" />
                    </motion.button>
                  )}
                </div>
              </>
            ) : (
              /* Completion Screen */
              <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6 }}
                  className="w-24 h-24 bg-gradient-to-br from-emerald-500/30 to-teal-500/30 rounded-full flex items-center justify-center mb-10"
                >
                  <Sparkles size={40} className="text-emerald-300" />
                </motion.div>
                
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="font-heading text-3xl text-white mb-4"
                >
                  {sessionData?.completion_message || "Your state has been reclaimed"}
                </motion.h2>
                
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-white/50 text-lg mb-12"
                >
                  {sessionData?.completion_submessage || "Notice how you feel now."}
                </motion.p>

                {/* 3-tap micro-rating — quietly trains per-user
                    calibration of max_affirmations so the next
                    session self-tunes to the user's nervous system. */}
                <SessionFeedbackCard
                  sessionId={sessionData?.session_id}
                  mode={sessionData?.mode}
                  activity={sessionData?.activity}
                  focusArea={sessionData?.focus_area}
                />

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Button
                    onClick={() => navigate("/")}
                    className="bg-white text-[#1a1a2e] font-medium px-12 py-4 rounded-full text-lg"
                    data-testid="complete-session-btn"
                  >
                    Return Home
                  </Button>
                </motion.div>
              </div>
            )}
          </motion.div>
        )}

        {/* Step 4.5: Pre-recorded Audio Player (Competition/Range Practice) */}
        {step === 4.5 && preRecordedAudioUrl && (
          <motion.div
            key="prerecorded-player"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="flex-1 flex flex-col min-h-screen"
            style={{
              background: signatureMode
                ? 'linear-gradient(180deg, #12100a 0%, #1a1408 40%, #12100a 100%)'
                : 'linear-gradient(180deg, #0a1628 0%, #0f1f3a 50%, #0a1628 100%)',
            }}
          >
            {/* Header */}
            <div className="p-6 flex items-center justify-between">
              <button 
                onClick={() => {
                  if (signatureMode && signatureData?.has_focus_areas) {
                    setStep(1.6);
                  } else if (signatureMode) {
                    setStep(1.5);
                  } else {
                    setStep(2);
                  }
                  setPreRecordedAudioUrl(null);
                  setSignatureMode(false);
                  setSelectedFocusAreaName(null);
                }}
                className="text-white/60 hover:text-white"
                data-testid="player-back-btn"
              >
                <ArrowLeft size={24} />
              </button>
              {!signatureMode && (
                <span className="text-white/60 text-sm">{preRecordedAudioDuration}</span>
              )}
              {signatureMode && (
                <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: 'rgba(251,191,36,0.4)' }}>
                  Signature Mode
                </span>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col items-center justify-center px-6">
              {/* Visual */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="relative w-48 h-48 mb-8"
              >
                {signatureMode ? (
                  <>
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.15) 0%, transparent 70%)' }}
                      animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.8, 0.5] }}
                      transition={{ duration: 6, repeat: 999, repeatType: "loop" }}
                    />
                    <motion.div
                      className="absolute inset-6 rounded-full"
                      style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.1) 0%, transparent 70%)' }}
                      animate={{ scale: [1.1, 1, 1.1], opacity: [0.3, 0.6, 0.3] }}
                      transition={{ duration: 4, repeat: 999, repeatType: "loop" }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Sparkles size={48} style={{ color: 'rgba(251,191,36,0.5)' }} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/30 to-teal-500/30 rounded-full blur-3xl" />
                    <div className="absolute inset-4 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-full blur-xl" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Flag size={64} className="text-emerald-400" />
                    </div>
                  </>
                )}
              </motion.div>

              {/* Title */}
              {signatureMode && signatureData ? (
                <>
                  <h2 className="text-2xl text-white mb-2 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {signatureData.name}{selectedFocusAreaName ? ` — ${selectedFocusAreaName}` : ''}
                  </h2>
                  <p className="text-sm italic text-center mb-3" style={{ fontFamily: "'Spectral', serif", color: 'rgba(251,191,36,0.4)' }}>
                    {signatureData.tagline}
                  </p>
                  {/* Arc phases */}
                  <div className="flex items-center gap-2 mb-8">
                    {(signatureData.arc || []).map((phase, i) => (
                      <span key={i} className="flex items-center gap-2">
                        <span className="text-[10px] capitalize" style={{ color: 'rgba(251,191,36,0.3)' }}>{phase}</span>
                        {i < signatureData.arc.length - 1 && (
                          <span className="text-[8px]" style={{ color: 'rgba(255,255,255,0.1)' }}>→</span>
                        )}
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-white mb-2 text-center">
                    {preRecordedAudioLabel}
                  </h2>
                  <p className="text-white/50 text-center mb-8">
                    {activities.find(a => a.id === selectedActivity)?.label || 'Performance'} • Subliminal
                  </p>
                </>
              )}

              {/* Audio Player */}
              <div className="w-full max-w-md">
                <audio 
                  controls 
                  className="w-full" 
                  src={`${process.env.REACT_APP_BACKEND_URL}${preRecordedAudioUrl}`}
                  autoPlay
                  data-testid="signature-audio-player"
                >
                  Your browser does not support the audio element.
                </audio>
              </div>

              {/* Instructions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-8 p-4 rounded-2xl max-w-md"
                style={{
                  background: signatureMode ? 'rgba(251,191,36,0.04)' : 'rgba(255,255,255,0.05)',
                  border: signatureMode ? '1px solid rgba(251,191,36,0.08)' : '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <p className="text-sm text-center" style={{ color: signatureMode ? 'rgba(251,191,36,0.45)' : 'rgba(255,255,255,0.7)' }}>
                  Put on your headphones and relax. The affirmations are embedded at subliminal volume — 
                  just below conscious perception. Let the music guide your state.
                </p>
                {signatureMode && (
                  <p className="text-[10px] text-center mt-3" style={{ color: 'rgba(255,255,255,0.15)' }}>
                    Refined through performance state modelling
                  </p>
                )}
              </motion.div>

              {/* Done button */}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                onClick={() => {
                  setStep(signatureMode ? (signatureData?.has_focus_areas ? 1.6 : 0) : 1);
                  setPreRecordedAudioUrl(null);
                  setSignatureMode(false);
                  setSelectedFocusAreaName(null);
                }}
                className="mt-8 px-8 py-3 rounded-full transition-colors"
                style={{
                  background: signatureMode ? 'rgba(251,191,36,0.1)' : 'rgba(16,185,129,0.2)',
                  border: signatureMode ? '1px solid rgba(251,191,36,0.2)' : '1px solid rgba(16,185,129,0.3)',
                  color: signatureMode ? 'rgba(251,191,36,0.6)' : 'rgb(52,211,153)',
                }}
                data-testid="player-done-btn"
              >
                Done
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Step 5: Session History */}
        {step === 5 && (
          <motion.div
            key="history"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="flex-1 flex flex-col"
          >
            <div className="p-6">
              <button onClick={() => setStep(0)} className="text-white/60 hover:text-white">
                <ArrowLeft size={24} />
              </button>
            </div>
            
            <div className="px-6 pb-6">
              <h2 className="font-heading text-3xl text-white mb-2">Session History</h2>
              <p className="text-white/50 text-sm">Your Private Suite journey</p>
            </div>
            
            {historyLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-white/20 border-t-white/80 rounded-full animate-spin" />
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto px-6 pb-8">
                {/* Insights Card */}
                {insights?.has_insights && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 rounded-2xl p-5 mb-6 border border-violet-500/20"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp size={18} className="text-violet-400" />
                      <span className="text-white font-medium">Your Patterns</span>
                    </div>
                    
                    {/* Stats */}
                    <div className="flex gap-6 mb-4">
                      <div>
                        <p className="text-2xl font-heading text-white">{insights.total_sessions_this_week}</p>
                        <p className="text-white/40 text-xs">sessions this week</p>
                      </div>
                      <div>
                        <p className="text-2xl font-heading text-white">{insights.total_minutes_this_week}</p>
                        <p className="text-white/40 text-xs">minutes</p>
                      </div>
                    </div>
                    
                    {/* Patterns */}
                    {insights.patterns?.map((pattern, idx) => (
                      <div key={idx} className="mb-3">
                        <p className="text-white/80 text-sm">{pattern.message}</p>
                      </div>
                    ))}
                    
                    {/* Recommendations */}
                    {insights.recommendations?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <div className="flex items-start gap-2">
                          <AlertCircle size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
                          <p className="text-white/60 text-sm">
                            {insights.recommendations[0].text}
                          </p>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
                
                {/* Session List */}
                {sessionHistory.length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-4">Recent Sessions</p>
                    {sessionHistory.map((session, idx) => {
                      const ModeIcon = getModeIcon(session);
                      const title = getSessionTitle(session);
                      const subtitle = getSessionSubtitle(session);
                      const modeColor = getModeColor(session);
                      
                      return (
                        <motion.div
                          key={session.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className="bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors"
                          data-testid={`session-history-${session.id}`}
                        >
                          <div className="flex items-start gap-4">
                            {/* Mode Icon */}
                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${modeColor} flex items-center justify-center flex-shrink-0`}>
                              <ModeIcon size={22} className={`${
                                session.mode === "performance" ? "text-orange-300" :
                                session.mode === "deep_recovery" ? "text-slate-300" : "text-violet-300"
                              }`} />
                            </div>
                            
                            {/* Session Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className="text-white font-medium text-sm truncate">{title}</h4>
                                <span className="text-white/40 text-xs ml-2 flex-shrink-0">
                                  {session.duration_seconds 
                                    ? `${Math.floor(session.duration_seconds / 60)} min` 
                                    : (session.duration === 'continuous' ? '∞' : session.duration)}
                                </span>
                              </div>
                              
                              {subtitle && (
                                <p className="text-white/50 text-xs mb-1.5 truncate">{subtitle}</p>
                              )}
                              
                              <div className="flex items-center gap-3">
                                <p className="text-white/30 text-xs">
                                  {session.completed_at ? new Date(session.completed_at).toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric'
                                  }) : 'Unknown date'}
                                </p>
                                
                                {/* Session tags */}
                                <div className="flex items-center gap-1.5">
                                  {session.subliminal_mode && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-violet-500/20 text-violet-300 rounded">
                                      Subliminal
                                    </span>
                                  )}
                                  {session.post_session_feeling && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 rounded capitalize">
                                      {session.post_session_feeling.replace(/_/g, ' ')}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Replay Button */}
                            <button
                              onClick={() => startReplaySession(session.id)}
                              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors flex-shrink-0"
                              data-testid={`replay-session-${session.id}`}
                            >
                              <Play size={18} className="text-white ml-0.5" />
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
                    <History size={40} className="text-white/20 mb-4" />
                    <p className="text-white/50 mb-2">No sessions yet</p>
                    <p className="text-white/30 text-sm">Complete your first session to see your history</p>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Milestone Celebration Modal */}
      <AchievementModal
        isOpen={showMilestoneCelebration}
        onClose={async () => {
          setShowMilestoneCelebration(false);
          // Acknowledge the milestone so it won't show again
          if (newMilestone) {
            try {
              await axios.post(
                `${API_URL}/api/private-suite/milestone/acknowledge/${newMilestone}`,
                {},
                { headers: { Authorization: `Bearer ${getToken()}` } }
              );
            } catch (err) {
              console.error("Error acknowledging milestone:", err);
            }
          }
          setNewMilestone(null);
        }}
        milestone={newMilestone}
        totalMinutes={totalMinutesForMilestone}
        isNewAchievement={true}
      />
      
      {/* First-Time Onboarding */}
      <AnimatePresence>
        {showOnboarding && (
          <PrivateSuiteOnboarding
            onComplete={handleOnboardingComplete}
            onSkip={handleOnboardingSkip}
          />
        )}
      </AnimatePresence>
      
      {/* Save as State Modal - shown after first session */}
      <AnimatePresence>
        {showSaveState && (
          <SaveAsState
            desiredState={desiredState}
            sessionConfig={{
              mode,
              affirmations: editableAffirmations.filter(a => a.enabled).map(a => a.text),
              duration: 15,
              voiceType: 'reclaim',
              subliminal: subliminalMode
            }}
            onSave={handleSaveState}
            onSkip={() => setShowSaveState(false)}
            onClose={() => setShowSaveState(false)}
          />
        )}
      </AnimatePresence>
      
      {/* Tune In To Reality - Archetype State Engine */}
      <AnimatePresence>
        {showRealityFlow && (
          <TuneInToReality
            onClose={handleCloseRealityFlow}
            savedStates={savedRealityStates}
            onSaveState={handleSaveRealityState}
          />
        )}
      </AnimatePresence>
      
      {/* My States Dashboard */}
      <AnimatePresence>
        {showMyStates && (
          <MyStates
            onClose={() => setShowMyStates(false)}
            getToken={getToken}
          />
        )}
      </AnimatePresence>
      
      {/* Floating Home Button for navigation */}
      <FloatingHomeButton />
    </div>
  );
};

// Wrapper component with error handling for Safari
const PrivateSuitePageWrapper = () => {
  const [hasError, setHasError] = useState(false);
  const navigate = useNavigate();
  
  if (hasError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a1a2e] to-[#16213e] flex flex-col items-center justify-center p-6">
        <div className="text-center">
          <Sparkles size={48} className="text-violet-400 mx-auto mb-4" />
          <h2 className="text-white text-xl font-semibold mb-2">Something went wrong</h2>
          <p className="text-white/60 mb-6">Please try refreshing the page</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-violet-600 text-white px-6 py-3 rounded-xl mr-3"
          >
            Refresh
          </button>
          <button 
            onClick={() => navigate('/')}
            className="bg-white/10 text-white px-6 py-3 rounded-xl"
          >
            Go Home
          </button>
        </div>
        <BottomNav />
      </div>
    );
  }
  
  try {
    return (
      <>
        <PrivateSuitePage />
        <BottomNav />
      </>
    );
  } catch (error) {
    console.error("PrivateSuite error:", error);
    setHasError(true);
    return null;
  }
};

export default PrivateSuitePageWrapper;
